// Moment-by-moment data for the /home-key lessons — Units 1–5 so far, later
// units get appended here. Everything is plain serializable data: demo
// phrases are composed in the phrase model ({ notes: [{ midi, at, dur }] },
// ms timings), patches are referenced by PATCHES name, and try/build checks
// reference the CHECKS registry in homeKeyTheory by name.
//
// Musical ground rules for authored phrases: stay inside the on-screen piano
// (G3..G5, midi 55..79), mostly stepwise, a gentle ~90bpm feel, note durations
// 300–700ms. Useful midis: G3=55 A3=57 B3=59 C4=60 D4=62 E4=64 F#4=66 G4=67
// A4=69 B4=71 C5=72 D5=74 E5=76 F#5=78 G5=79.

import { chordMidis, chordPhrase } from "./homeKeyTheory";

// Tiny builders so the phrase data below stays readable.
const n = (midi, at, dur, vel) => (vel == null ? { midi, at, dur } : { midi, at, dur, vel });
const ph = (...notes) => ({ notes });

// Chord progressions rendered as data. 650ms chords with a small breath keep
// every authored chord note inside the 300–700ms "gentle durations" rule
// (the live builder loops at homeKeyTheory's roomier default instead).
const prog = (degrees, opts) =>
  chordPhrase(degrees, { chordMs: 650, gapMs: 150, ...(opts || {}) });

// One chord restated at several moments — a slow pulse to hum under a melody
// inside a single demo phrase. Returns bare notes for spreading into ph().
const chordHits = (degree, ats, dur = 700) =>
  ats.flatMap((at) => chordMidis(degree).map((midi) => ({ midi, at, dur })));

/* ============================================================
   Composed demo phrases
   ============================================================ */

// 1.1 — a little wander that lands on home (G3) and rings.
const WANDER_HOME = ph(
  n(62, 0, 400),
  n(64, 450, 400),
  n(66, 900, 400),
  n(64, 1350, 400),
  n(62, 1800, 400),
  n(60, 2250, 400),
  n(59, 2700, 400),
  n(57, 3150, 400),
  n(55, 3600, 700)
);

// 1.1 — the same melody twice; only the final note differs.
const ENDING_BODY = [
  n(64, 0, 400),
  n(62, 450, 400),
  n(60, 900, 400),
  n(59, 1350, 400),
  n(60, 1800, 400),
  n(59, 2250, 400),
];
const MELODY_LANDED = ph(...ENDING_BODY, n(55, 2700, 700)); // ends on G3 — home
const MELODY_FLOATING = ph(...ENDING_BODY, n(57, 2700, 700)); // stops on A3 — midi 57

// 1.1 echo — two-note calls falling to G; the last call ends off-home so the
// learner finishes it (finisher).
const CALL_FALL_A = ph(n(57, 0, 450), n(55, 500, 650));
const CALL_FALL_B = ph(n(59, 0, 450), n(55, 500, 650));
const CALL_FALL_HIGH = ph(n(69, 0, 450), n(67, 500, 650));
const CALL_UNFINISHED = ph(n(59, 0, 450), n(57, 500, 650)); // lands on 2 — finish it

// 1.2 — the scale rising 1..7 and home again, played over the drone.
const SCALE_RISE = ph(
  n(55, 0, 350),
  n(57, 400, 350),
  n(59, 800, 350),
  n(60, 1200, 350),
  n(62, 1600, 350),
  n(64, 2000, 350),
  n(66, 2400, 350),
  n(67, 2800, 700)
);

// 1.2 — the climb that stalls on 7 vs. the climb that resolves 7 → 1.
const CLIMB_BODY = [
  n(55, 0, 400),
  n(57, 450, 400),
  n(59, 900, 400),
  n(60, 1350, 400),
  n(62, 1800, 400),
  n(64, 2250, 400),
];
const CLIMB_STALLED = ph(...CLIMB_BODY, n(66, 2700, 700)); // hangs on the doorbell
const CLIMB_RESOLVED = ph(...CLIMB_BODY, n(66, 2700, 400), n(67, 3150, 700));

// 1.2 echo — three-note calls ending on different degrees.
const CALL_END_1 = ph(n(59, 0, 400), n(57, 450, 400), n(55, 900, 650)); // ends 1
const CALL_END_2 = ph(n(60, 0, 400), n(59, 450, 400), n(57, 900, 650)); // ends 2
const CALL_END_5 = ph(n(59, 0, 400), n(60, 450, 400), n(62, 900, 650)); // ends 5
const CALL_END_7 = ph(n(62, 0, 400), n(64, 450, 400), n(66, 900, 650)); // ends 7

// 1.3 — a fixed 2-bar question, then three answers that differ only in their
// last note: 5 (comma), 7 (cliffhanger), 1 (period).
const QUESTION = [
  n(59, 0, 400),
  n(62, 450, 400),
  n(64, 900, 400),
  n(66, 1350, 400),
  n(64, 1800, 600),
];
const ANSWER_BODY = [n(62, 2700, 400), n(60, 3150, 400), n(59, 3600, 400)];
const QA_COMMA = ph(...QUESTION, ...ANSWER_BODY, n(62, 4050, 700)); // ends on 5
const QA_CLIFFHANGER = ph(...QUESTION, ...ANSWER_BODY, n(66, 4050, 700)); // ends on 7
const QA_PERIOD = ph(...QUESTION, ...ANSWER_BODY, n(55, 4050, 700)); // ends on 1

// 1.3 echo — 4–5-note calls landing home.
const CALL_HOME_A = ph(n(59, 0, 400), n(57, 450, 400), n(59, 900, 400), n(57, 1350, 400), n(55, 1800, 700));
const CALL_HOME_B = ph(n(62, 0, 400), n(60, 450, 400), n(59, 900, 400), n(57, 1350, 400), n(55, 1800, 700));
const CALL_HOME_C = ph(n(62, 0, 400), n(64, 450, 400), n(66, 900, 400), n(67, 1350, 700));

/* ------------------------------------------------------------
   Unit 2 — Distances
   ------------------------------------------------------------ */

// 2.1 — mostly steps, one leap spent on the lift up to 6, then a walk home.
const SL_ONE_LEAP = ph(
  n(55, 0, 400),
  n(57, 450, 400),
  n(59, 900, 400),
  n(57, 1350, 400),
  n(64, 1800, 500),
  n(62, 2350, 400),
  n(60, 2800, 400),
  n(59, 3250, 400),
  n(57, 3700, 400),
  n(55, 4150, 700)
);

// 2.1 — the same rhythm twice: A walks in steps, B leaps every single move.
const SL_WALK = ph(
  n(60, 0, 400),
  n(62, 450, 400),
  n(64, 900, 400),
  n(62, 1350, 400),
  n(60, 1800, 400),
  n(59, 2250, 400),
  n(57, 2700, 400),
  n(55, 3150, 700)
);
const SL_LEAPS = ph(
  n(60, 0, 400),
  n(72, 450, 400),
  n(64, 900, 400),
  n(71, 1350, 400),
  n(62, 1800, 400),
  n(69, 2250, 400),
  n(59, 2700, 400),
  n(55, 3150, 700)
);

// 2.1 echo — an all-steps stroll, a one-leap lift, a settling drop.
const CALL_SL_STEPS = ph(n(59, 0, 400), n(60, 450, 400), n(62, 900, 400), n(60, 1350, 400), n(59, 1800, 650));
const CALL_SL_LIFT = ph(n(55, 0, 400), n(57, 450, 400), n(59, 900, 400), n(64, 1350, 400), n(62, 1800, 650));
const CALL_SL_DROP = ph(n(67, 0, 400), n(64, 450, 400), n(62, 900, 400), n(60, 1350, 400), n(59, 1800, 650));

// 2.2 — home (G4) reaching out to a few of its distances in turn.
const CD_PALETTE = ph(
  n(67, 0, 400),
  n(71, 450, 400),
  n(67, 900, 400),
  n(74, 1350, 400),
  n(67, 1800, 400),
  n(76, 2250, 400),
  n(67, 2700, 400),
  n(79, 3150, 700)
);

// 2.2 — the same move at two distances: home out to a 3rd vs out to a 7th.
const CD_THIRD = ph(n(67, 0, 500), n(71, 600, 700));
const CD_SEVENTH = ph(n(67, 0, 500), n(78, 600, 700));

// 2.2 echo — little calls that each live inside one distance.
const CALL_CD_THIRD = ph(n(67, 0, 400), n(71, 450, 400), n(67, 900, 650));
const CALL_CD_FIFTH = ph(n(67, 0, 400), n(74, 450, 400), n(67, 900, 650));
const CALL_CD_SIXTH = ph(n(67, 0, 400), n(76, 450, 400), n(67, 900, 650));
const CALL_CD_OCTAVE = ph(n(67, 0, 400), n(79, 450, 400), n(67, 900, 650));

// 2.3 — two notes one after another, then the same two at once: harmony born.
const TA_BORN = ph(n(67, 0, 600), n(71, 700, 600), n(67, 1500, 700), n(71, 1500, 700));

// 2.3 — a wide-open fifth vs neighbors leaning on each other (both in-scale).
const TA_BLEND = ph(n(67, 0, 700), n(74, 0, 700));
const TA_CRUNCH = ph(n(66, 0, 700), n(67, 0, 700));

// 2.3 echo — short calls that land on a two-note hit.
const CALL_TA_SWEET = ph(n(62, 0, 400), n(64, 450, 400), n(67, 900, 650), n(71, 900, 650));
const CALL_TA_HOME = ph(n(59, 0, 400), n(57, 450, 400), n(55, 900, 650), n(62, 900, 650));

/* ------------------------------------------------------------
   Unit 3 — Melody Shapes
   ------------------------------------------------------------ */

// 3.1 — a slow arch drawn in the air: up one side, over the top, back down.
const CT_SKETCH = ph(
  n(55, 0, 400),
  n(57, 450, 400),
  n(60, 900, 400),
  n(64, 1350, 400),
  n(67, 1800, 500),
  n(64, 2350, 400),
  n(60, 2800, 400),
  n(57, 3250, 400),
  n(55, 3700, 700)
);

// 3.1 — one rhythm, three drawings: arch (peak mid), ramp (peak last),
// dive (peak first).
const CT_ARCH = ph(
  n(55, 0, 400),
  n(59, 450, 400),
  n(62, 900, 400),
  n(67, 1350, 400),
  n(62, 1800, 400),
  n(59, 2250, 400),
  n(55, 2700, 700)
);
const CT_RAMP = ph(
  n(55, 0, 400),
  n(57, 450, 400),
  n(59, 900, 400),
  n(60, 1350, 400),
  n(62, 1800, 400),
  n(64, 2250, 400),
  n(67, 2700, 700)
);
const CT_DIVE = ph(
  n(67, 0, 400),
  n(64, 450, 400),
  n(62, 900, 400),
  n(60, 1350, 400),
  n(59, 1800, 400),
  n(57, 2250, 400),
  n(55, 2700, 700)
);

// 3.1 echo — three little drawings to trace by hand.
const CALL_CT_ARCH = ph(n(59, 0, 400), n(62, 450, 400), n(66, 900, 400), n(62, 1350, 400), n(59, 1800, 650));
const CALL_CT_WAVE = ph(n(62, 0, 400), n(64, 450, 400), n(62, 900, 400), n(60, 1350, 400), n(62, 1800, 650));
const CALL_CT_DIVE = ph(n(71, 0, 400), n(67, 450, 400), n(64, 900, 400), n(62, 1350, 400), n(59, 1800, 650));

// 3.2 — the same little motif said twice: exactly (hypnotic), then with a
// changed ending (alive). Only the final note differs.
const SAY_EXACT = ph(
  n(62, 0, 400),
  n(64, 450, 400),
  n(62, 900, 400),
  n(59, 1350, 400),
  n(62, 2250, 400),
  n(64, 2700, 400),
  n(62, 3150, 400),
  n(59, 3600, 700)
);
const SAY_VARIED = ph(
  n(62, 0, 400),
  n(64, 450, 400),
  n(62, 900, 400),
  n(59, 1350, 400),
  n(62, 2250, 400),
  n(64, 2700, 400),
  n(62, 3150, 400),
  n(55, 3600, 700)
);

// 3.2 echo — say a bit, say it again, land somewhere new.
const CALL_SA_TWIN = ph(
  n(59, 0, 400),
  n(62, 450, 400),
  n(59, 900, 400),
  n(59, 1500, 400),
  n(62, 1950, 400),
  n(55, 2400, 650)
);
const CALL_SA_HIGH = ph(
  n(67, 0, 400),
  n(64, 450, 400),
  n(67, 900, 400),
  n(67, 1500, 400),
  n(64, 1950, 400),
  n(62, 2400, 650)
);

// 3.3 — a two-bar question that rises away from home, alone…
const QA_HALF = ph(
  n(62, 0, 400),
  n(64, 450, 400),
  n(66, 900, 400),
  n(64, 1350, 400),
  n(62, 1800, 400),
  n(69, 2250, 700)
);
// …and the whole thought: the same question plus an answer that walks home.
const QA_WHOLE = ph(
  n(62, 0, 400),
  n(64, 450, 400),
  n(66, 900, 400),
  n(64, 1350, 400),
  n(62, 1800, 400),
  n(69, 2250, 600),
  n(67, 3300, 400),
  n(66, 3750, 400),
  n(64, 4200, 400),
  n(62, 4650, 400),
  n(60, 5100, 400),
  n(59, 5550, 400),
  n(57, 6000, 400),
  n(55, 6450, 700)
);

// 3.3 echo — questions to answer, not echo: one hangs on the doorbell (7),
// one stops a single step short of home (2).
const CALL_QA_DOORBELL = ph(n(59, 0, 400), n(62, 450, 400), n(64, 900, 400), n(66, 1350, 650));
const CALL_QA_SHORT = ph(n(62, 0, 400), n(60, 450, 400), n(59, 900, 400), n(57, 1350, 650));

/* ------------------------------------------------------------
   Unit 4 — Chords & Their Jobs
   ------------------------------------------------------------ */

// 4.1 — home alone, then the stack assembling around it: 1, then 1+3, then
// the full 1+3+5 ringing as one new sound.
const STACK_ASSEMBLE = ph(
  n(55, 0, 600),
  n(55, 800, 600),
  n(59, 800, 600),
  n(55, 1600, 700),
  n(59, 1600, 700),
  n(62, 1600, 700)
);

// 4.1 — the same three notes taking turns (a walk) vs speaking at once (a chord).
const STACK_TURNS = ph(n(55, 0, 450), n(59, 500, 450), n(62, 1000, 650));
const STACK_TOGETHER = ph(
  n(55, 0, 650),
  n(59, 0, 650),
  n(62, 0, 650),
  n(55, 900, 700),
  n(59, 900, 700),
  n(62, 900, 700)
);

// 4.1 echo — walk the stack, then grab it whole (three fingers, one moment).
const CALL_STACK_WALK = ph(n(55, 0, 400), n(59, 450, 400), n(62, 900, 650));
const CALL_STACK_GRAB = ph(
  n(55, 0, 400),
  n(59, 450, 400),
  n(62, 900, 400),
  n(55, 1500, 650),
  n(59, 1500, 650),
  n(62, 1500, 650)
);

// 4.2 — the ladder: all seven chords in a row, then home again.
const CHORD_LADDER = prog([1, 2, 3, 4, 5, 6, 7, 1]);

// 4.2 — same neighborhood, different weather: the 1 chord vs the 6, twice each.
const WEATHER_SUN = prog([1, 1]);
const WEATHER_DUSK = prog([6, 6]);

// 4.2 echo — the two weathers under the hands: down the 1 chord, up the 6.
const CALL_SUN = ph(n(62, 0, 400), n(59, 450, 400), n(55, 900, 650));
const CALL_DUSK = ph(n(59, 0, 400), n(64, 450, 400), n(67, 900, 650));

// 4.3 — a whole trip in four chords: home → away → tension → home.
const TRIP_DEMO = prog([1, 6, 5, 1]);

// 4.3 — the doorbell (7, F#4) alone, then the 5 chord blooming around it —
// the Unit 1 callback: the itch you already know lives inside this chord.
const DOORBELL_INSIDE = ph(
  n(66, 0, 650),
  n(57, 800, 700),
  n(62, 800, 700),
  n(66, 800, 700)
);

// 4.3 — the 5 chord leaning, twice… then answered (the doorbell F# steps up
// onto home's G, the 1 chord opening the door) vs stalling (5, forever).
const FIVE_RESOLVES = ph(
  n(57, 0, 600),
  n(62, 0, 600),
  n(66, 0, 600),
  n(57, 750, 600),
  n(62, 750, 600),
  n(66, 750, 600),
  n(55, 1500, 700),
  n(59, 1500, 700),
  n(62, 1500, 700),
  n(67, 1500, 700)
);
const FIVE_STALLS = ph(
  n(57, 0, 600),
  n(62, 0, 600),
  n(66, 0, 600),
  n(57, 750, 600),
  n(62, 750, 600),
  n(66, 750, 600),
  n(57, 1500, 700),
  n(62, 1500, 700),
  n(66, 1500, 700)
);

// 4.3 echo — riding the pull home: the doorbell stepping up into 1.
const CALL_PULL_STEP = ph(n(66, 0, 400), n(67, 450, 650));
const CALL_PULL_RIDE = ph(n(62, 0, 400), n(66, 450, 400), n(67, 900, 650));

// 4.4 — the famous four once through; the loop cycling twice (it hands the
// tension back to its own top); the journey landing on home and stopping.
const LOOP_ONCE = prog([6, 4, 1, 5]);
const LOOP_CYCLING = prog([6, 4, 1, 5, 6, 4, 1, 5]);
const JOURNEY_LANDS = prog([1, 4, 5, 1]);

// 4.4 echo — each trip's ground floor: the bottom note of every chord.
const CALL_LOOP_ROOTS = ph(n(64, 0, 400), n(60, 450, 400), n(55, 900, 400), n(62, 1350, 650));
const CALL_JOURNEY_ROOTS = ph(n(55, 0, 400), n(60, 450, 400), n(62, 900, 400), n(55, 1350, 650));

/* ------------------------------------------------------------
   Unit 5 — Melody Meets Harmony
   ------------------------------------------------------------ */

// 5.1 — the home chord pulsing while a melody walks over it: one melody made
// only of the chord's own notes (it melts), one that never touches them (it
// fights). Same rhythm, same chord, opposite weather.
const GLUE_PULSE = chordHits(1, [0, 800, 1600, 2400]);
const MELODY_MELTS = ph(
  ...GLUE_PULSE,
  n(67, 0, 350),
  n(71, 400, 350),
  n(74, 800, 550),
  n(71, 1600, 350),
  n(67, 2400, 700)
);
const MELODY_FIGHTS = ph(
  ...GLUE_PULSE,
  n(69, 0, 350),
  n(72, 400, 350),
  n(76, 800, 550),
  n(72, 1600, 350),
  n(69, 2400, 700)
);

// 5.1 tinker — a melody mixing glue and sparkle over the humming 1 chord.
const GLUE_MIX = ph(
  n(67, 0, 400),
  n(69, 450, 400),
  n(71, 900, 400),
  n(72, 1350, 400),
  n(71, 1800, 400),
  n(74, 2250, 550),
  n(72, 2900, 400),
  n(71, 3350, 400),
  n(67, 3800, 700)
);

// 5.2 — a walk between targets over the pulsing 1 chord: chord tones on the
// landings, passing notes in the strides. Two endings: square on a stone the
// chord owns (D) vs stopped in the gap between stones (C).
const WALK_PULSE = chordHits(1, [0, 900, 1800]);
const WALK_BODY = [n(67, 0, 400), n(69, 450, 400), n(71, 900, 400), n(72, 1350, 400)];
const WALK_SOLID = ph(...WALK_PULSE, ...WALK_BODY, n(74, 1800, 700));
const WALK_BETWEEN = ph(...WALK_PULSE, ...WALK_BODY, n(72, 1800, 700));

// 5.2 tinker — the same walk, up and back down, to poke at over the chord.
const WALK_ROUND = ph(
  n(67, 0, 400),
  n(69, 450, 400),
  n(71, 900, 400),
  n(72, 1350, 400),
  n(74, 1800, 550),
  n(72, 2400, 400),
  n(71, 2850, 400),
  n(69, 3300, 400),
  n(67, 3750, 700)
);

// 5.2 echo — little lines that each land on a note the 1 chord owns (G, D, B).
const CALL_LAND_G = ph(n(60, 0, 400), n(59, 450, 400), n(57, 900, 400), n(55, 1350, 650));
const CALL_LAND_D = ph(n(69, 0, 400), n(71, 450, 400), n(72, 900, 400), n(74, 1350, 650));
const CALL_LAND_B = ph(n(74, 0, 400), n(72, 450, 400), n(71, 900, 650));

// 5.3 — a fixed example of the rewrite: the famous loop with a melody aiming
// a landing at each chord as it passes (targets on the chords, passing notes
// between), ending on the doorbell inside the 5 so the loop stays hungry.
const REWRITE_CHORDS = [
  ...chordHits(6, [0]),
  ...chordHits(4, [900]),
  ...chordHits(1, [1800]),
  ...chordHits(5, [2700]),
];
const REWRITE_EXAMPLE = ph(
  ...REWRITE_CHORDS,
  n(76, 0, 550),
  n(74, 600, 300),
  n(72, 900, 550),
  n(71, 1500, 300),
  n(67, 1800, 550),
  n(69, 2400, 300),
  n(66, 2700, 700)
);

// 5.3 — same chords, same melody, two last notes: one dropped into the home
// chord's glove (G, a note the chord owns), one slipping just past it (A).
const CATCH_CHORDS = [...chordHits(1, [0]), ...chordHits(5, [900]), ...chordHits(1, [1800])];
const CATCH_BODY = [n(71, 0, 400), n(69, 450, 400), n(66, 900, 550)];
const LANDING_CAUGHT = ph(...CATCH_CHORDS, ...CATCH_BODY, n(67, 1800, 700));
const LANDING_SLIPS = ph(...CATCH_CHORDS, ...CATCH_BODY, n(69, 1800, 700));

/* ------------------------------------------------------------
   Unit 6 — The Minor Side
   ------------------------------------------------------------ */

// A drone floor carried as data, so a single listen option can hum its own
// home: one long, quiet low note under the melody. 43 (G2) and 40 (E2) are
// the startDrone pitches — deliberately below the on-screen piano, and the
// authored phrases' one sanctioned exception to the range/duration ground
// rules (the colocated tests scope that exemption to exactly these notes).
const droneBed = (midi, durMs) => n(midi, 0, durMs, 0.8);

// 6.1 — a wander over the same seven notes that settles, this time, on E.
const DUSK_WANDER = ph(
  n(67, 0, 400),
  n(66, 450, 400),
  n(64, 900, 400),
  n(62, 1350, 400),
  n(64, 1800, 400),
  n(66, 2250, 400),
  n(67, 2700, 400),
  n(64, 3150, 700)
);

// 6.1 — the two-house phrase: the identical melody over two different
// floors. It ends on E — the wistful 6 in G's house, home itself in E's.
const TWO_HOUSE_MELODY = [
  n(71, 0, 400),
  n(69, 450, 400),
  n(67, 900, 400),
  n(69, 1350, 400),
  n(66, 1800, 400),
  n(64, 2250, 700),
];
const HOUSE_OF_G = ph(droneBed(43, 3400), ...TWO_HOUSE_MELODY);
const HOUSE_OF_E = ph(droneBed(40, 3400), ...TWO_HOUSE_MELODY);

// 6.1 echo — little falls that all settle on the new home.
const CALL_DUSK_STEP = ph(n(66, 0, 450), n(64, 500, 650)); // 2 leaning onto 1
const CALL_DUSK_SIGH = ph(n(67, 0, 450), n(64, 500, 650)); // the old home, sighing down
const CALL_DUSK_HIGH = ph(n(78, 0, 450), n(76, 500, 650)); // the same fall, upstairs
const CALL_DUSK_WALK = ph(n(71, 0, 400), n(69, 450, 400), n(67, 900, 400), n(64, 1350, 650));

// 6.2 — the whole ladder of chords, renumbered around the shadow home.
const MINOR_LADDER = prog([1, 2, 3, 4, 5, 6, 7, 1], { mode: "minor" });

// 6.2 — home, the 5, home — twice over. The ONLY difference between these
// two demos is one note in the middle chord: the neighborhood's own D in
// the natural 5, the borrowed D# in the raised one. (A test holds us to
// exactly that one-note difference.)
const BELL_NATURAL = prog([1, 5, 1], { mode: "minor" });
const BELL_BORROWED = prog([1, "5+", 1], { mode: "minor" });

// 6.3 — the same trip in two weathers: matching degrees on both sides of
// the key, with the borrowed doorbell doing the 5's job at dusk.
const TRIP_AFTERNOON = prog([1, 6, 4, 5]);
const TRIP_DUSK = prog([1, 6, 4, "5+"], { mode: "minor" });

/* ------------------------------------------------------------
   Units 7–8 — Groove & Bass / Layers, Dynamics & Sound
   ------------------------------------------------------------ */

// Stack helper: one chord voicing struck at one moment, as bare notes for
// spreading into ph() (the Unit 7–8 demos lean on chord stabs).
const stack = (midis, at, dur = 700) => midis.map((midi) => n(midi, at, dur));

// 7.1 — the pulse at its smallest: one note keeping four steady beats.
const GRID_PULSE = ph(
  n(55, 0, 400),
  n(55, 600, 400),
  n(55, 1200, 400),
  n(55, 1800, 400),
  n(55, 2400, 700)
);

// 7.1 — the same riff twice, note for note: A sits exactly on the grid, B
// drags every offbeat 110ms late. Identical midis and durations — only the
// offbeat `at`s move. (A test holds us to exactly that.)
const GROOVE_RIFF_MIDIS = [55, 59, 62, 59, 60, 62, 59, 57, 55];
const RIFF_STRAIGHT = ph(
  ...GROOVE_RIFF_MIDIS.map((midi, i) => n(midi, i * 300, i === 8 ? 700 : 300))
);
const RIFF_SWUNG = ph(
  ...GROOVE_RIFF_MIDIS.map((midi, i) =>
    n(midi, i * 300 + (i % 2 === 1 ? 110 : 0), i === 8 ? 700 : 300)
  )
);

// 7.2 — the same home chord as two pulses: claps on the answering beats
// (2 and 4 of two bars, beat = 600ms) vs a stamp on every single beat.
const STAB_CLAP = ph(...chordHits(1, [600, 1800, 3000, 4200], 300));
const STAB_MARCH = ph(
  ...chordHits(1, [0, 600, 1200, 1800, 2400, 3000, 3600, 4200], 300)
);

// 7.2 echo — the piano as a drum kit: low = kick, middle = snare, high = hat.
const CALL_FLOORS = ph(n(55, 0, 350), n(55, 1200, 350)); // beats 1 and 3
const CALL_CLAPS = ph(n(55, 0, 300), n(67, 600, 300), n(55, 1200, 300), n(67, 1800, 300));
const CALL_TICKS = ph(n(74, 0, 300), n(74, 600, 300), n(74, 1200, 300), n(74, 1800, 300));

// 7.3 — a bass pacing its room: low, patient, mostly one note.
const BASS_PACE = ph(
  n(55, 0, 350),
  n(55, 600, 350),
  n(55, 1200, 350),
  n(57, 1800, 350),
  n(59, 2400, 350),
  n(59, 3000, 350),
  n(60, 3600, 350),
  n(55, 4200, 600)
);

// 7.3 — four chords overhead (voiced high, midi 66+, so the bass keeps the
// floor to itself), one bar each: 1, 4, 6, 5 — roots G, C, E, D. Under them,
// the same bass rhythm twice: A stands on each bar's root; B wanders and
// never once touches it. The chords and the rhythm are identical.
const BASS_LOOP_CHORDS = [
  ...stack([67, 71, 74], 0), // the 1 chord — root G
  ...stack([67, 72, 76], 1200), // the 4 — root C
  ...stack([67, 71, 76], 2400), // the 6 — root E
  ...stack([66, 69, 74], 3600), // the 5 — root D
];
const BASS_ROOTED = ph(
  ...BASS_LOOP_CHORDS,
  n(55, 0, 350),
  n(55, 600, 350),
  n(60, 1200, 350),
  n(60, 1800, 350),
  n(64, 2400, 350),
  n(64, 3000, 350),
  n(62, 3600, 350),
  n(62, 4200, 500)
);
const BASS_WANDER = ph(
  ...BASS_LOOP_CHORDS,
  n(62, 0, 350),
  n(64, 600, 350),
  n(59, 1200, 350),
  n(64, 1800, 350),
  n(60, 2400, 350),
  n(62, 3000, 350),
  n(57, 3600, 350),
  n(60, 4200, 500)
);

// 7.3 echo — bass hands: pump a root, walk up to a door, walk back home.
const CALL_ROOT_PUMP = ph(n(55, 0, 350), n(55, 600, 350), n(55, 1200, 350), n(55, 1800, 500));
const CALL_ROOT_WALK = ph(n(55, 0, 350), n(57, 450, 350), n(59, 900, 350), n(60, 1350, 500));
const CALL_ROOT_DROP = ph(n(62, 0, 350), n(60, 450, 350), n(59, 900, 350), n(55, 1350, 500));

// 8.1 — the same mid-lane chords with company, mixed two ways: everything
// crowded into one octave (60–69) vs the parts spread into lanes — a
// basement bass, the chords mid, a melody upstairs.
const MID_CHORD = [62, 66, 69];
const MIX_CLUTTER = ph(
  ...stack(MID_CHORD, 0, 700),
  ...stack(MID_CHORD, 1200, 700),
  n(64, 0, 300),
  n(66, 350, 300),
  n(67, 700, 300),
  n(66, 1050, 300),
  n(64, 1400, 300),
  n(62, 1750, 300),
  n(60, 150, 300),
  n(62, 500, 300),
  n(64, 850, 300),
  n(60, 1550, 300)
);
const MIX_SPACED = ph(
  ...stack(MID_CHORD, 0, 700),
  ...stack(MID_CHORD, 1200, 700),
  n(55, 0, 400),
  n(55, 600, 400),
  n(55, 1200, 400),
  n(55, 1800, 400),
  n(74, 0, 300),
  n(76, 350, 300),
  n(74, 700, 300),
  n(71, 1050, 400),
  n(74, 1400, 300),
  n(71, 1750, 400)
);

// 8.1 echo — the same kind of line at two altitudes.
const CALL_LOW_LANE = ph(n(55, 0, 400), n(57, 500, 400), n(55, 1000, 600));
const CALL_HIGH_LANE = ph(n(74, 0, 350), n(76, 400, 350), n(74, 800, 350), n(71, 1200, 600));

// 8.2 — energy is density: four bars (1000ms each) that keep adding — one
// note, then chords, then melody, then everything — vs the same amount of
// music every single bar.
const GROW_DENSITY = ph(
  n(55, 0, 500),
  n(55, 1000, 500),
  ...stack([62, 66], 1000, 600),
  n(55, 2000, 500),
  ...stack([62, 66], 2000, 600),
  n(71, 2000, 300),
  n(74, 2350, 300),
  n(55, 3000, 400),
  n(55, 3500, 400),
  ...stack([62, 66, 69], 3000, 600),
  n(74, 3000, 300),
  n(76, 3350, 300),
  n(74, 3700, 300)
);
const FLAT_DENSITY = ph(
  ...[0, 1000, 2000, 3000].flatMap((at) => [n(55, at, 500), ...stack([62, 66], at, 600)])
);

// 8.2 echo — the same idea getting fuller, call by call.
const CALL_ARC_SPARSE = ph(n(55, 0, 400), n(62, 600, 600));
const CALL_ARC_BUILDING = ph(n(55, 0, 350), n(59, 400, 350), n(62, 800, 350), n(64, 1200, 500));
const CALL_ARC_FULL = ph(
  n(55, 0, 300),
  n(59, 350, 300),
  n(62, 700, 300),
  n(64, 1050, 300),
  n(67, 1400, 400),
  n(71, 1400, 400)
);

// 8.3 — one two-chord idea (home, then its sad twin) wearing two outfits:
// long floating strokes vs quick driving stabs. The pitch sets are identical
// note for note — only the delivery changes. (A test holds us to that.)
const HOME_TRIAD = [55, 59, 62];
const TWIN_TRIAD = [64, 67, 71];
const DRESS_GOWN = ph(
  ...stack(HOME_TRIAD, 0, 700),
  ...stack(HOME_TRIAD, 1400, 700),
  ...stack(TWIN_TRIAD, 2800, 700),
  ...stack(TWIN_TRIAD, 4200, 700)
);
const DRESS_DENIM = ph(
  ...[0, 400, 800, 1200].flatMap((at) => stack(HOME_TRIAD, at, 300)),
  ...[1600, 2000, 2400, 2800].flatMap((at) => stack(TWIN_TRIAD, at, 300))
);

// 8.4 — two four-section songs (1200ms bars, two strums each): one never
// leaves the sunny room; one steps into the shadow for its third stretch
// and comes back, so the return means something.
const strumBar = (midis, barAt) => [
  ...stack(midis, barAt, 650),
  ...stack(midis, barAt + 600, 650),
];
const ARR_ONE_WEATHER = ph(
  ...strumBar(HOME_TRIAD, 0),
  ...strumBar(HOME_TRIAD, 1200),
  ...strumBar(HOME_TRIAD, 2400),
  ...strumBar(HOME_TRIAD, 3600)
);
const ARR_LEAVES_HOME = ph(
  ...strumBar(HOME_TRIAD, 0),
  ...strumBar(HOME_TRIAD, 1200),
  ...strumBar(TWIN_TRIAD, 2400),
  ...strumBar(HOME_TRIAD, 3600)
);

/* ============================================================
   Lesson moments
   ============================================================ */

export const LESSON_MOMENTS = {
  /* ---- 1.1 The Note That Feels Like Home ---- */
  "home-note": [
    {
      id: "hn-welcome",
      type: "explain",
      body:
        "Almost all music is about one thing: leaving home and coming back. " +
        "Before chords, before scales, before anything — there's one note that " +
        "feels like home. Let's find yours.",
      demo: { kind: "phrase", phrase: WANDER_HOME, patch: "softKeys" },
    },
    {
      id: "hn-finished",
      type: "listen",
      prompt: "Same melody, two endings. Which one feels finished?",
      options: [
        { id: "a", label: "A", phrase: MELODY_LANDED },
        { id: "b", label: "B", phrase: MELODY_FLOATING },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "honestly, can't tell yet" },
      ],
      responses: {
        a:
          "Most ears land right where yours did. That settled feeling is the " +
          "melody coming to rest on a note called G — and for this whole " +
          "course, G is home.",
        b:
          "Your ear went the other way — good, that means you're actually " +
          "listening. Most ears feel A settle, because A comes to rest on a " +
          "note called G — the note this melody treats as home. Play them " +
          "back to back once more and see if B now feels like it stops one " +
          "step shy of the door.",
        unsure:
          "Totally fair — they're twins until the last moment. Play them " +
          "again back to back and listen just to the very last note of each: " +
          "one sits down, one hovers. Then pick whichever fits.",
      },
      note: "There's no wrong answer here — you're just meeting your own ear.",
    },
    {
      id: "hn-tonic",
      type: "explain",
      body:
        "G isn't special by itself. It becomes home because everything around " +
        "it points back to it. Musicians call the home note the tonic. We'll " +
        "mostly call it home, or 1.",
      demo: { kind: "drone" },
    },
    {
      id: "hn-gravity",
      type: "tinker",
      variant: "gravity",
      config: {
        drone: true,
        // Tension per degree while held over the drone (0 = at rest, 1 = max
        // pull). Out-of-scale notes read high but not maximal.
        tension: { 1: 0.05, 2: 0.55, 3: 0.2, 4: 0.65, 5: 0.25, 6: 0.45, 7: 0.9, out: 0.8 },
        prompt:
          "The drone is on. Play anything — watch (and feel) how hard each " +
          "note pulls back toward home.",
      },
    },
    {
      id: "hn-echo",
      type: "echo",
      prompt:
        "Echo each little call back. The last one stops short of home — " +
        "finish it for us: bring it home.",
      calls: [CALL_FALL_A, CALL_FALL_B, CALL_FALL_HIGH, CALL_UNFINISHED],
      finisher: true,
    },
    {
      id: "hn-phrase",
      type: "explain",
      body:
        "A short run of notes is a phrase — a musical sentence. The last note " +
        "is the punctuation: it decides period, comma, or cliffhanger.",
    },
    {
      id: "hn-keeper",
      type: "try",
      instructions:
        "Wander wherever you like over the drone — one rule: end on home and " +
        "let it ring.",
      checks: [
        { id: "enough-notes", check: "minNotes", args: [3] },
        {
          id: "ends-home",
          check: "endsOnDegree",
          args: [1],
          message:
            "That ending floats. Lovely — but this keeper wants a landing. " +
            "Bring it home, or keep it as-is?",
        },
      ],
      keeperId: "homecoming",
      keeperName: "the homecoming",
      withDrone: true,
    },
    {
      id: "hn-outro",
      type: "explain",
      body:
        "You just made the first music of your track. It's saved. Next door: " +
        "home has seven neighbors, and every one of them has a personality.",
    },
  ],

  /* ---- 1.2 The Seven Neighbors ---- */
  "seven-neighbors": [
    {
      id: "sn-neighborhood",
      type: "explain",
      body:
        "Home isn't alone. Seven notes live in its neighborhood — the G major " +
        "scale — and each one sits a different distance from home.",
      demo: { kind: "track", phrase: SCALE_RISE, patch: "softKeys", drone: true },
    },
    {
      id: "sn-freeplay",
      type: "tinker",
      variant: "tour",
      config: {
        mode: "freeplay",
        prompt:
          "The neighborhood notes glow with their numbers, 1 to 7. The dim " +
          "keys live outside the neighborhood — not forbidden, just visiting.",
      },
    },
    {
      id: "sn-doorbell",
      type: "listen",
      prompt:
        "Two climbs up the neighborhood, nearly identical. After one of " +
        "them you can finally relax; the other leaves you hanging. Which " +
        "one lets you breathe out?",
      options: [
        { id: "a", label: "A", phrase: CLIMB_STALLED },
        { id: "b", label: "B", phrase: CLIMB_RESOLVED },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "both leave me hanging" },
      ],
      responses: {
        a:
          "Interesting — A stops on the 7th note, the doorbell, and most " +
          "ears feel that as an itch rather than a rest. Maybe you exhaled " +
          "because the climbing stopped. B goes one note further — 7 " +
          "stepping home to 1 — and that step is the relief. Either way, " +
          "remember the itch: it powers every chord progression you'll " +
          "ever write.",
        b:
          "That last little step — 7 leaning into 1 — is the breath out. A " +
          "stops on the doorbell and just… rings. Remember that " +
          "itch-and-answer: it powers every chord progression you'll ever " +
          "write.",
        unsure:
          "Then you've already met the doorbell! Play them again and stay " +
          "with the very last note of each climb: one hangs in the air, one " +
          "steps down onto the landing. Your shoulders will tell you before " +
          "your brain does.",
      },
    },
    {
      id: "sn-personas",
      type: "tinker",
      variant: "tour",
      config: {
        mode: "personas",
        prompt:
          "Tap each degree and let it sit over the drone. Every neighbor has " +
          "a house-name — see if you agree with ours.",
      },
    },
    {
      id: "sn-echo",
      type: "echo",
      prompt:
        "Echo each call back, then ask yourself one thing: did it end " +
        "settled, or restless? (Just an impression — no wrong answers.)",
      calls: [CALL_END_1, CALL_END_2, CALL_END_5, CALL_END_7],
      reflect: ["settled", "restless"],
    },
    {
      id: "sn-keeper",
      type: "try",
      instructions:
        "Two doors: make one tiny phrase that ends restless — on 2, 5, or 7. " +
        "Unfinished business is exactly what a track needs.",
      checks: [
        { id: "enough-notes", check: "minNotes", args: [2] },
        {
          id: "ends-restless",
          check: "endsOnAnyDegree",
          args: [[2, 5, 7]],
          message:
            "That one settles right back home. Cozy! But this keeper wants an " +
            "open door — try ending on 2, 5, or 7. Or keep the cozy one if " +
            "it's speaking to you.",
        },
      ],
      keeperId: "openDoor",
      keeperName: "the open door",
      withDrone: true,
    },
    {
      id: "sn-outro",
      type: "explain",
      body:
        "Now you can hear what each note wants. Next: putting wanting and " +
        "getting in a row — that's all a melody is.",
    },
  ],

  /* ---- 1.3 Leaving and Returning ---- */
  "leaving-returning": [
    {
      id: "lr-conversation",
      type: "explain",
      body:
        "A melody is a conversation with home: a phrase that asks and a " +
        "phrase that answers. You already know how endings work. Let's write " +
        "both halves.",
    },
    {
      id: "lr-punctuation",
      type: "listen",
      prompt:
        "One question, three answers — same sentence, different " +
        "punctuation. One of these endings is a period: a full stop, " +
        "completely done. Which?",
      options: [
        { id: "a", label: "A", phrase: QA_COMMA },
        { id: "b", label: "B", phrase: QA_CLIFFHANGER },
        { id: "c", label: "C", phrase: QA_PERIOD },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "c", text: "C" },
        { id: "unsure", text: "can't hear a full stop yet" },
      ],
      responses: {
        a:
          "Your ear caught a real pause — A does stop, but it stops the " +
          "way a comma does: resting on 5, with clearly more to say. The " +
          "full stop is C, landing on 1 so the room can go quiet. And B " +
          "ends on 7 — a cliffhanger, a doorbell nobody answers. Three " +
          "endings, three punctuation marks.",
        b:
          "B is the boldest stop of the three — but it's a cliffhanger, " +
          "not a period: it ends on 7, the doorbell, and leaves you " +
          "leaning in for more. Good ear for feeling its drama, though. " +
          "The full stop is C, landing on 1 so the room can go quiet — " +
          "and A is the third mark, a comma resting on 5 with more to say.",
        c:
          "That's the one — C lands on 1, the sentence closes, and you " +
          "can breathe. Now the other two have names too: A ends on 5, a " +
          "comma — a pause with more to say. B ends on 7, a cliffhanger — " +
          "the doorbell rings and nobody answers.",
        unsure:
          "Fair — three in a row is a lot to hold. Play them again and " +
          "ignore everything except the very last note of each: which one " +
          "could you fall asleep after? That one's the period.",
      },
    },
    {
      id: "lr-ending-drag",
      type: "tinker",
      variant: "endingDrag",
      config: {
        // The question + answer, with the answer's final note (dragIndex) the
        // only movable one — drag it among scale tones and replay.
        phrase: QA_COMMA,
        dragIndex: 8,
        scaleOnly: true,
        prompt:
          "The question is fixed. Drag the answer's last note up and down " +
          "the scale, replay, and feel the punctuation change.",
      },
    },
    {
      id: "lr-echo",
      type: "echo",
      prompt: "Longer calls now — echo each one back, all the way home.",
      calls: [CALL_HOME_A, CALL_HOME_B, CALL_HOME_C],
    },
    {
      id: "lr-keeper",
      type: "try",
      instructions:
        "Your home call: about two bars, 3–6 notes, end on home. Play it in " +
        "— and if performing feels scary, slow counts. Slow is beautiful.",
      checks: [
        { id: "enough-notes", check: "minNotes", args: [3] },
        { id: "not-too-many", check: "maxNotes", args: [6] },
        {
          id: "ends-home",
          check: "endsOnDegree",
          args: [1],
          message:
            "This one's your track's seed — it wants to land on home so " +
            "everything can grow around it. One more take? Or keep the drift.",
        },
      ],
      keeperId: "homeCall",
      keeperName: "your home call",
      withDrone: true,
    },
    {
      id: "lr-outro",
      type: "explain",
      body:
        "This little phrase is going to grow chords under it, a bassline, " +
        "drums, a second voice. That's the rest of the course. Welcome home.",
      // The player resolves the learner's saved keeper at runtime and loops
      // it over the drone — the first "your track so far" playback.
      demo: { kind: "track", keeperId: "homeCall", patch: "softKeys", drone: true },
    },
  ],

  /* ---- 2.1 Steps & Leaps ---- */
  "steps-and-leaps": [
    {
      id: "sl-two-moves",
      type: "explain",
      body:
        "From any note, there are only two ways to go: to a neighbor, or past " +
        "one. A step is small and smooth — it talks the way people talk. A " +
        "leap jumps over the fence, and everyone hears it. Two moves, two " +
        "completely different feelings.",
      demo: { kind: "phrase", phrase: SL_ONE_LEAP, patch: "softKeys" },
    },
    {
      id: "sl-ab",
      type: "listen",
      prompt:
        "Same rhythm, same landing note. One of these only ever steps — " +
        "every move to a next-door neighbor. Which one?",
      options: [
        { id: "a", label: "A", phrase: SL_WALK },
        { id: "b", label: "B", phrase: SL_LEAPS },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "they blur together" },
      ],
      responses: {
        a:
          "That's the stroll — every note in A holds hands with the next. " +
          "B never touches a neighbor: every single move vaults a fence. " +
          "Thrilling for a moment, exhausting for a melody — which is why " +
          "most melodies live the way A does: mostly steps, singing the " +
          "way we speak.",
        b:
          "Your ear went to B — and B is the wilder one: every move leaps " +
          "clean over a neighbor, and all that drama can make it feel like " +
          "the main event. The all-steps one is A, every note holding " +
          "hands with the next. Play them again back to back — A strolls, " +
          "B parkours.",
        unsure:
          "They share every drumbeat, so that's fair. Play them once more " +
          "and listen to the distance between notes instead of the notes " +
          "themselves: one melody walks to the house next door, the other " +
          "pole-vaults over it.",
      },
      note: "Neither is wrong — they're two tools. The trick is the mix.",
    },
    {
      id: "sl-economy",
      type: "explain",
      body:
        "Here's the working rule almost every melody follows: mostly steps, " +
        "with leaps spent like money. A leap costs something — attention, " +
        "drama, breath — so melodies save them for the moment that matters, " +
        "then step their way back down to earth.",
    },
    {
      id: "sl-feel",
      type: "tinker",
      variant: "tour",
      config: {
        mode: "freeplay",
        prompt:
          "Take a walk: neighbor to neighbor along the glowing keys. Then, " +
          "when you feel like it, jump a fence — one big leap — and feel how " +
          "much louder that move is than all the steps around it.",
      },
    },
    {
      id: "sl-echo",
      type: "echo",
      prompt:
        "Echo each call back. One strolls, one spends a single leap, one " +
        "settles down from up high — feel the difference in your fingers.",
      calls: [CALL_SL_STEPS, CALL_SL_LIFT, CALL_SL_DROP],
    },
    {
      id: "sl-keeper",
      type: "try",
      instructions:
        "Take a walk over the drone: mostly steps, and spend at most one " +
        "leap — save it for the moment that deserves it.",
      checks: [
        { id: "enough-notes", check: "minNotes", args: [4] },
        {
          id: "spend-wisely",
          check: "maxLeaps",
          args: [1],
          message:
            "That's a lot of fences jumped! This keeper is a walk — mostly " +
            "steps, one leap at most. Smooth it out, or keep the parkour if " +
            "it's saying what you mean.",
        },
      ],
      keeperId: "smoothWalk",
      keeperName: "the smooth walk",
      withDrone: true,
    },
    {
      id: "sl-outro",
      type: "explain",
      body:
        "Steps and leaps are how far a melody moves. Next: what each " +
        "distance actually tastes like — because a leap of a 3rd and a leap " +
        "of a 7th are entirely different creatures.",
    },
  ],

  /* ---- 2.2 The Colors of Distance ---- */
  "colors-of-distance": [
    {
      id: "cd-flavors",
      type: "explain",
      body:
        "In Unit 1 every neighbor had a personality. Distances have them " +
        "too: the gap between two notes carries its own flavor, no matter " +
        "where it happens. Musicians call these gaps intervals. We'll call " +
        "them what they feel like.",
      demo: { kind: "phrase", phrase: CD_PALETTE, patch: "softKeys" },
    },
    {
      id: "cd-tour-melodic",
      type: "tinker",
      variant: "intervalTour",
      config: {
        mode: "melodic",
        prompt:
          "Tap each distance to hear home step out to it. A 2nd rubs, a 3rd " +
          "is sweet, a 4th opens, a 5th stands strong, a 6th aches warmly, a " +
          "7th reaches, and the octave is home's own mirror. See if your " +
          "ears agree.",
      },
    },
    {
      id: "cd-ab",
      type: "listen",
      prompt:
        "Two reaches out from home, one small and one enormous. One of " +
        "them stretches up on tiptoe — fingers almost touching something, " +
        "not quite. Which one?",
      options: [
        { id: "a", label: "A", phrase: CD_THIRD },
        { id: "b", label: "B", phrase: CD_SEVENTH },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "both just sound like two notes" },
      ],
      responses: {
        a:
          "A has its own kind of reach — but it steps into the garden next " +
          "door: a 3rd, sweet and easy, still in sight of home. The tiptoe " +
          "one is B — a 7th, stretching nearly all the way to home's " +
          "mirror and stopping just short. Play them again and feel how " +
          "much farther B leaves you leaning.",
        b:
          "That's the 7th — reaching nearly all the way to the mirror and " +
          "stopping just short, fingers not quite touching. That almost is " +
          "its whole flavor. A is the cozy one: a 3rd, one garden over — " +
          "the friendliest distance in music.",
        unsure:
          "Then let the size sneak up on you: play A and hum its top note, " +
          "then play B and try to hum its top note. One of them makes your " +
          "voice climb way past comfortable — that's the tiptoe.",
      },
      note: "No wrong answers — you're building a taste for distance.",
    },
    {
      id: "cd-two-ways",
      type: "explain",
      body:
        "You've been hearing distances as moves — one note, then the other. " +
        "But every distance has a second life: sound both notes at once and " +
        "the move becomes a color. Same gap, deeper flavor.",
    },
    {
      id: "cd-tour-harmonic",
      type: "tinker",
      variant: "intervalTour",
      config: {
        mode: "harmonic",
        prompt:
          "The same distances, now both notes at once. The rub really rubs, " +
          "the sweet gets sweeter, the mirror melts into one note. Find the " +
          "one that makes your chest do something.",
      },
    },
    {
      id: "cd-echo",
      type: "echo",
      prompt:
        "Echo these little calls — each one lives inside a single distance: " +
        "a sweet 3rd, a strong 5th, a warm-ache 6th, a mirror octave.",
      calls: [CALL_CD_THIRD, CALL_CD_FIFTH, CALL_CD_SIXTH, CALL_CD_OCTAVE],
    },
    {
      id: "cd-keeper",
      type: "try",
      instructions:
        "Pick the distance that spoke to you most — that's yours now. Make " +
        "a short phrase that keeps coming back to it: home out to it, it " +
        "back to home, however you like.",
      checks: [
        { id: "enough-notes", check: "minNotes", args: [3] },
        { id: "keep-it-short", check: "maxNotes", args: [10] },
      ],
      keeperId: "favoriteDistance",
      keeperName: "your favorite distance",
      withDrone: true,
    },
    {
      id: "cd-outro",
      type: "explain",
      body:
        "You just heard it: two notes at once isn't a bigger melody — it's " +
        "a brand-new thing. Next door, we give that thing its name.",
    },
  ],

  /* ---- 2.3 Two at Once ---- */
  "two-at-once": [
    {
      id: "ta-born",
      type: "explain",
      body:
        "Play one note, then another: that's melody. Play them at the same " +
        "time and something brand-new appears in the air between them — a " +
        "third sound neither note makes alone. That's harmony, being born.",
      demo: { kind: "phrase", phrase: TA_BORN, patch: "softKeys" },
    },
    {
      id: "ta-ab",
      type: "listen",
      prompt:
        "Two pairs of notes, each pair sounding together. One pair buzzes " +
        "— you can feel the air between the notes shiver. Which one?",
      options: [
        { id: "a", label: "A", phrase: TA_BLEND },
        { id: "b", label: "B", phrase: TA_CRUNCH },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "they both just sound like sound" },
      ],
      responses: {
        a:
          "Your ear went to the big open one — A is a fifth, two notes " +
          "agreeing so completely they nearly become one bigger note, and " +
          "that pure hugeness can register as intensity. The literal buzz " +
          "lives in B: two next-door neighbors one tiny step apart, the " +
          "air between them shivering. Play them back to back once more — " +
          "you won't un-hear it now.",
        b:
          "That shiver is real — B is two next-door neighbors leaning on " +
          "the same fence, and the air between them trembles. That's " +
          "dissonance, and it isn't a mistake: it's a spice. A is its " +
          "opposite — a wide-open fifth, so agreeable the two notes nearly " +
          "become one. That's consonance: notes at rest with each other.",
        unsure:
          "Hold each one a little longer, then. Play A and let it ring; " +
          "play B and let it ring. One sits still in the air; the other " +
          "wobbles, like two tones arm-wrestling. The wobble is the buzz.",
      },
      note:
        "Neither is better. Blend with no buzz gets bland; dream pop drizzles " +
        "the crunch on almost everything.",
    },
    {
      id: "ta-spice",
      type: "explain",
      body:
        "So pairs of notes sit somewhere between blending and buzzing, and " +
        "every spot on that line is useful. Smooth pairs are the walls of a " +
        "song; crunchy pairs are the doorways — tension you can walk " +
        "through to get somewhere sweet.",
    },
    {
      id: "ta-pairs",
      type: "tinker",
      variant: "intervalTour",
      config: {
        mode: "harmonic",
        prompt:
          "All seven distances again, as pairs over the drone. Hunt for the " +
          "crunchiest one and the smoothest one — then decide which one " +
          "you'd want humming under your melody.",
      },
    },
    {
      id: "ta-echo",
      type: "echo",
      prompt:
        "Echo these — careful, each one ends on two keys at once. Two " +
        "fingers, one moment. Your first played harmony.",
      calls: [CALL_TA_SWEET, CALL_TA_HOME],
    },
    {
      id: "ta-keeper",
      type: "try",
      instructions:
        "Time to not play alone. Choose the color that hums underneath — a " +
        "sweet third or a strong fifth — then record a little melody over " +
        "it. Listen to how your notes shake hands with the held one.",
      bed: {
        prompt: "what hums under you? (tap to hear it)",
        choices: [
          { id: "third", label: "a sweet third", midis: [59] },
          { id: "fifth", label: "a strong fifth", midis: [62] },
        ],
      },
      checks: [{ id: "enough-notes", check: "minNotes", args: [3] }],
      keeperId: "firstHarmony",
      keeperName: "your first harmony",
      withDrone: true,
    },
    {
      id: "ta-outro",
      type: "explain",
      body:
        "Distances, colors, and now two notes sounding as one thing — " +
        "that's the whole seed of harmony. Next unit: the shapes melodies " +
        "draw when they stretch their legs.",
    },
  ],

  /* ---- 3.1 Melodies Are Drawings ---- */
  contour: [
    {
      id: "ct-drawings",
      type: "explain",
      body:
        "Squint at any melody and the notes blur into a line: it rises, " +
        "falls, arcs, dives. That line is the contour — the drawing — and " +
        "your ear catches the drawing before it catches a single note.",
      demo: { kind: "phrase", phrase: CT_SKETCH, patch: "softKeys" },
    },
    {
      id: "ct-abc",
      type: "listen",
      prompt:
        "One rhythm, three drawings. One of them is an arch — up one side, " +
        "over the top, back down. Which one? (Listen for where the highest " +
        "note lives.)",
      options: [
        { id: "a", label: "A", phrase: CT_ARCH },
        { id: "b", label: "B", phrase: CT_RAMP },
        { id: "c", label: "C", phrase: CT_DIVE },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "c", text: "C" },
        { id: "unsure", text: "the shapes blur together" },
      ],
      responses: {
        a:
          "That's the arch — A puts its peak in the middle: rise, touch " +
          "the top, come home. It breathes like a sentence, which is why " +
          "so many melodies are arches. The other two drawings: B is a " +
          "ramp, saving its peak for the very last note and ending leaned " +
          "forward; C is a dive, spending its peak immediately and then " +
          "exhaling all the way down.",
        b:
          "B climbs the whole way — which is half of an arch, so your ear " +
          "was onto something real. But B never comes down: its peak is " +
          "the very last note. That shape is a ramp — all climb, ending " +
          "leaned forward, asking for what's next. The full arch is A: up, " +
          "over the top in the middle, back home. And C is a dive — peak " +
          "first, then one long exhale.",
        c:
          "C has the arch's falling half — a graceful descent — so your " +
          "ear caught real arch DNA. But C starts at its peak: that's a " +
          "dive — grand entrance, gentle landing. The full arch is A: up " +
          "one side, over the top in the middle, down the other. B is the " +
          "third shape, a ramp that climbs to its peak and stops.",
        unsure:
          "Draw them, honestly — put a hand in the air while each one " +
          "plays and follow the pitch. One hand traces a hill, one climbs " +
          "stairs, one slides down a slide. The hill is the arch.",
      },
      note:
        "No best shape — but notice how the peak's address changes the " +
        "feeling of the whole line.",
    },
    {
      id: "ct-peak",
      type: "explain",
      body:
        "The highest note is the most expensive real estate in a phrase — " +
        "the ear treats it as the destination. Touch it once and the line " +
        "has a summit. Touch it three times and it has a plateau: pleasant, " +
        "but nothing points anywhere.",
    },
    {
      id: "ct-echo",
      type: "echo",
      prompt:
        "Echo these little drawings — an arch, a wave, a dive. Don't chase " +
        "the exact notes; trace the shape with your hand.",
      calls: [CALL_CT_ARCH, CALL_CT_WAVE, CALL_CT_DIVE],
    },
    {
      id: "ct-keeper",
      type: "try",
      instructions:
        "Draw one line with one clear summit: rise to your highest note " +
        "exactly once, then come down the other side — or hang up there, " +
        "your call. Just make sure the line has a single top.",
      checks: [
        { id: "enough-notes", check: "minNotes", args: [4] },
        { id: "one-summit", check: "singlePeak", args: [] },
      ],
      keeperId: "arcLine",
      keeperName: "the arc line",
      withDrone: true,
    },
    {
      id: "ct-outro",
      type: "explain",
      body:
        "Now your phrases have a skyline. Next: the strange magic that " +
        "happens when a melody repeats itself — almost.",
    },
  ],

  /* ---- 3.2 Say It Again, But Different ---- */
  "say-it-again": [
    {
      id: "sa-hook",
      type: "explain",
      body:
        "Here's the open secret of every hook you've ever loved: it says " +
        "something, then says it again. Repetition is how music teaches you " +
        "its language in real time — the second time through, you're " +
        "already singing along.",
      demo: { kind: "phrase", phrase: SAY_VARIED, patch: "softKeys" },
    },
    {
      id: "sa-ab",
      type: "listen",
      prompt:
        "Each of these says the same little idea twice. In one, the repeat " +
        "is a perfect twin — exact, note for note. Which one?",
      options: [
        { id: "a", label: "A", phrase: SAY_EXACT },
        { id: "b", label: "B", phrase: SAY_VARIED },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "the difference slipped past me" },
      ],
      responses: {
        a:
          "That's the twin — A repeats itself exactly, and exact " +
          "repetition is a spell: the phrase becomes a place you're " +
          "staying rather than a story moving on. Loops, chants, and " +
          "lullabies live there. B smuggles in one changed note at the " +
          "very end — and that tiny swerve is what turns a repeat into a " +
          "reply.",
        b:
          "B nearly fooled you on purpose — it matches its first half " +
          "note for note until the very last one, then swerves. So the " +
          "perfect twin is A. And honestly, the fact that B feels 'same' " +
          "right up until it isn't is the whole trick: your ear learns " +
          "the pattern, then catches the surprise. That little spark is " +
          "what 'catchy' is made of.",
        unsure:
          "The change is one single note, so no shame there. Play them " +
          "again and lean in on the very last note of each one's second " +
          "half — one lands where it landed before; the other lands " +
          "somewhere new. Once you hear it, it's huge.",
      },
      note: "Both are real tools. Exact repeats hold time still; varied ones push it forward.",
    },
    {
      id: "sa-motif",
      type: "explain",
      body:
        "A small idea worth repeating is called a motif. The recipe you " +
        "just heard — say it, say it again, change the ending — is the " +
        "oldest trick in melody, and it still works every single time.",
    },
    {
      id: "sa-drag",
      type: "tinker",
      variant: "endingDrag",
      config: {
        phrase: SAY_VARIED,
        dragIndex: 7,
        scaleOnly: true,
        prompt:
          "The first statement is fixed, and so is most of the second. Drag " +
          "just the final note: how far can the ending drift before the " +
          "repeat stops feeling like a repeat?",
      },
    },
    {
      id: "sa-echo",
      type: "echo",
      prompt:
        "Echo these twins — each call says a bit, says it again, and lands " +
        "somewhere new. Feel the moment the change arrives.",
      calls: [CALL_SA_TWIN, CALL_SA_HIGH],
    },
    {
      id: "sa-keeper",
      type: "try",
      instructions:
        "About two bars: state a little idea (three-ish notes), say it " +
        "again, and change how it ends. You agreeing with yourself, then " +
        "surprising yourself.",
      checks: [
        {
          id: "enough-notes",
          check: "minNotes",
          args: [6],
          message:
            "A twice-told idea usually needs six notes or so — three to say " +
            "it, three to say it again. Room for one more pass? Or keep the " +
            "haiku if it's complete.",
        },
      ],
      keeperId: "echoTwin",
      keeperName: "the echo twin",
      withDrone: true,
    },
    {
      id: "sa-outro",
      type: "explain",
      body:
        "Say it, say it again, change the ending — you now own the oldest " +
        "trick in the book. Next: putting two phrases in conversation, and " +
        "walking away with a whole melody.",
    },
  ],

  /* ---- 3.3 Question & Answer ---- */
  "question-answer": [
    {
      id: "qa-callback",
      type: "explain",
      body:
        "Way back in Unit 1 you recorded your home call — a little phrase " +
        "that knows how to land. Everything since has been sharpening it: " +
        "steps and leaps, distances, shapes, repeats. Today it all comes " +
        "together in the oldest structure in music: a question and its " +
        "answer.",
      demo: {
        kind: "track",
        keeperId: "homeCall",
        patch: "softKeys",
        drone: true,
        label: "hear your home call again",
      },
    },
    {
      id: "qa-halves",
      type: "listen",
      prompt:
        "Two takes on the same idea. One is a whole thought — it asks and " +
        "then answers. The other stops partway. Which one feels complete?",
      options: [
        { id: "a", label: "A", phrase: QA_HALF },
        { id: "b", label: "B", phrase: QA_WHOLE },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "neither settles for me" },
      ],
      responses: {
        a:
          "Your ear heard A close a chapter — and A does finish saying " +
          "something: it rises, steps away from home… and hangs there, " +
          "ears waiting. That's the question alone. B takes the same " +
          "question and answers it, walking all the way back home to 1. " +
          "Two phrases that need each other — that's a melody.",
        b:
          "That's the whole thought — B asks away from home, then answers " +
          "back to it, landing on 1 and letting it ring. A is only the " +
          "first half: the question, hanging mid-air with your ears " +
          "waiting. Two phrases that need each other — that's not a " +
          "fragment anymore, that's a melody.",
        unsure:
          "Try this: play each one, and the moment it ends, say 'the end' " +
          "out loud. After one of them, 'the end' feels like a lie; the " +
          "other earns it. The one that earns it is the whole thought.",
      },
      note: "Leaving and returning, one more time — just bigger now.",
    },
    {
      id: "qa-answer-echo",
      type: "echo",
      prompt:
        "Don't echo these — answer them. Each call asks and stops away from " +
        "home; you write the reply that brings it back to 1.",
      calls: [CALL_QA_DOORBELL, CALL_QA_SHORT],
      finisher: true,
    },
    {
      id: "qa-blueprint",
      type: "explain",
      body:
        "The blueprint, then: four bars, two halves. The question ends " +
        "anywhere but home — 2, 5, and the doorbell 7 all ask beautifully. " +
        "The answer ends on 1 and lets it ring. Stuck for a question? Your " +
        "home call already knows how to be an answer — write the question " +
        "it's been waiting for.",
    },
    {
      id: "qa-keeper",
      type: "try",
      instructions:
        "Your melody, part A: about four bars over the drone. Ask a " +
        "question that ends away from home, breathe, then answer it — land " +
        "on 1 and let it ring.",
      checks: [
        { id: "enough-notes", check: "minNotes", args: [6] },
        { id: "not-a-saga", check: "maxNotes", args: [24] },
        {
          id: "ends-home",
          check: "endsOnDegree",
          args: [1],
          message:
            "The question's still hanging! A whole melody wants its answer " +
            "home on 1. One more take to land it — or keep the cliffhanger " +
            "if that's the story you're telling.",
        },
      ],
      keeperId: "melodyA",
      keeperName: "your melody, part A",
      withDrone: true,
    },
    {
      id: "qa-outro",
      type: "explain",
      body:
        "Question, answer, shape, steps, one well-spent leap — that's a " +
        "real melody, and it's yours. Your track just found its voice. " +
        "Next unit: we stack the scale on itself and grow it some chords.",
      demo: { kind: "track", keeperId: "melodyA", patch: "softKeys", drone: true },
    },
  ],

  /* ---- 4.1 Three Notes, One Feeling ---- */
  stacking: [
    {
      id: "stk-intro",
      type: "explain",
      body:
        "You've heard two notes at once make a third thing appear between " +
        "them. Today: three. Take home, skip a neighbor, take the warm 3, " +
        "skip again, take 5 — stack them up and let them ring. Something " +
        "brand-new stands there.",
      demo: { kind: "phrase", phrase: STACK_ASSEMBLE, patch: "softKeys" },
    },
    {
      id: "stk-listen",
      type: "listen",
      prompt:
        "Same three notes, twice. Once they take turns; once they all " +
        "speak in the same moment — and fuse into a single new sound. " +
        "Which one is the new thing?",
      options: [
        { id: "a", label: "A", phrase: STACK_TURNS },
        { id: "b", label: "B", phrase: STACK_TOGETHER },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "they sound like the same thing" },
      ],
      responses: {
        a:
          "A is the trio taking turns — three little events in a row, which " +
          "is a melody. The new thing is B: the same notes sounding at " +
          "once, fusing into one sound none of them makes alone. That " +
          "fused thing is a chord — and it's what this whole unit is about.",
        b:
          "That's it — B fuses the three notes into one new creature: a " +
          "chord. Play A again and notice you hear three arrivals; play B " +
          "and you hear exactly one, with depth. Same ingredients, " +
          "completely different animal.",
        unsure:
          "Fair — same notes, so they rhyme hard. Try counting arrivals: A " +
          "hands you three little ones in a row; B hands you exactly one. " +
          "The one-arrival version is the chord.",
      },
      note: "Both are real music — a chord is just a melody holding hands.",
    },
    {
      id: "stk-recipe",
      type: "explain",
      body:
        "That new thing is a chord: three neighbors singing at once, heard " +
        "as one sound with a mood. The recipe never changes — take a note, " +
        "skip one, take one, skip one, take one. Start the stack anywhere " +
        "in the neighborhood and you get a different chord with a " +
        "different mood.",
    },
    {
      id: "stk-tour",
      type: "tinker",
      variant: "chordTour",
      config: {
        mode: "colors",
        degrees: [1, 4, 6],
        prompt:
          "Three chords to start with. Tap a number, let the stack ring, " +
          "and watch which keys it lives on — then play around them on the " +
          "piano. The rest of the family arrives next lesson.",
      },
    },
    {
      id: "stk-echo",
      type: "echo",
      prompt:
        "Echo the stack back: first as a walk up it, then — careful — the " +
        "last call ends on all three notes at once. Three fingers, one " +
        "moment: your first played chord.",
      calls: [CALL_STACK_WALK, CALL_STACK_GRAB],
    },
    {
      id: "stk-keeper",
      type: "build",
      variant: "progression",
      config: {
        slots: 1,
        prompt:
          "One slot. Tap through all seven chords and keep the one that " +
          "feels most like you today. There's no wrong mood — just yours.",
      },
      checks: [],
      keeperId: "firstChord",
      keeperName: "your first chord",
    },
    {
      id: "stk-outro",
      type: "explain",
      body:
        "Your track just grew its first chord. There are six more where " +
        "that came from — next door, we meet the whole family and give " +
        "each one its house-name.",
    },
  ],

  /* ---- 4.2 The Seven Chords of Home ---- */
  "seven-chords": [
    {
      id: "svc-family",
      type: "explain",
      body:
        "Every note in the neighborhood can host its own stack. Seven " +
        "notes, seven chords — a full palette that comes free with the " +
        "key. Here they all are, climbing from home back to home.",
      demo: { kind: "phrase", phrase: CHORD_LADDER, patch: "softKeys" },
    },
    {
      id: "svc-tour",
      type: "tinker",
      variant: "chordTour",
      config: {
        mode: "colors",
        prompt:
          "All seven, yours to wander. Tap each chord, let it ring, and " +
          "meet its house-name — then argue with our names. Yours might be " +
          "better.",
      },
    },
    {
      id: "svc-weather",
      type: "listen",
      prompt:
        "Two chords from the same neighborhood — they even share notes. " +
        "One feels like afternoon light; in the other, the lights just " +
        "dimmed. Which one dimmed?",
      options: [
        { id: "a", label: "A", phrase: WEATHER_SUN },
        { id: "b", label: "B", phrase: WEATHER_DUSK },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "the weather won't settle" },
      ],
      responses: {
        a:
          "Your ear went to A — and A is the 1 chord, home itself, the one " +
          "most ears file under sunlight. Worth another spin: B is the 6 " +
          "chord, and it shares two of home's three notes. Nearly the same " +
          "house, but the light through the windows has gone blue.",
        b:
          "That's the 6 chord — the sad twin. Here's the eerie part: it " +
          "shares two of home's three notes. Barely anything changed, and " +
          "the whole sky did. Bright-weather stacks get called major, dusk " +
          "ones minor — but you just heard the difference, which matters " +
          "more than the names.",
        unsure:
          "They're close kin, so that's fair. Play them again and watch " +
          "your chest instead of your ears: one chord lets it sit easy, " +
          "the other tugs it somewhere tender. The tug is the sad twin.",
      },
      note: "Same neighborhood, different weather — you'll want both.",
    },
    {
      id: "svc-twins",
      type: "explain",
      body:
        "The 1 and the 6 are the strangest pair in the palette: two of " +
        "their three notes are the same notes. That's why the 6 is home's " +
        "sad twin — same address, different weather. Every chord in the " +
        "family leans bright or dusk like this, and a song's mood is " +
        "mostly which ones it keeps reaching for.",
    },
    {
      id: "svc-echo",
      type: "echo",
      prompt:
        "Play the two weathers with your own hands: one call walks down " +
        "the sunny chord, one climbs the sad twin. Feel the sky change " +
        "under your fingers.",
      calls: [CALL_SUN, CALL_DUSK],
    },
    {
      id: "svc-keeper",
      type: "build",
      variant: "progression",
      config: {
        slots: 2,
        prompt:
          "Two slots: pick two chords that feel like opposites — your " +
          "today, in two weathers. Loop them and listen to them argue.",
      },
      checks: [],
      keeperId: "chordPortrait",
      keeperName: "your chord portrait",
    },
    {
      id: "svc-outro",
      type: "explain",
      body:
        "Seven moods, all yours now. But moods alone don't make music " +
        "move — chords also have jobs to do. Next: home, away, and the " +
        "ones whose whole job is tension.",
    },
  ],

  /* ---- 4.3 Home, Away, Tension ---- */
  "chord-jobs": [
    {
      id: "cj-trips",
      type: "explain",
      body:
        "A row of chords is called a progression, but hear it as a trip: " +
        "leave home, wander somewhere, feel something tug, come back. " +
        "Every chord in the palette works one of three jobs on that trip — " +
        "home, away, or tension.",
      demo: { kind: "phrase", phrase: TRIP_DEMO, patch: "softKeys" },
    },
    {
      id: "cj-jobs",
      type: "tinker",
      variant: "chordTour",
      config: {
        mode: "jobs",
        prompt:
          "Tap each chord, listen, and file it by feel — home, away, or " +
          "tension — before we tell you its job. Your ear goes first; the " +
          "names only ever come second.",
      },
    },
    {
      id: "cj-inside",
      type: "explain",
      body:
        "Remember the doorbell? The 7 note — the itch from the very first " +
        "unit that has to be answered. It never left. Listen: there it is, " +
        "ringing alone… and then the 5 chord blooms around it. That's the " +
        "5 chord's secret — it carries the doorbell in its pocket, which " +
        "is why it pulls home harder than anything else in the palette.",
      demo: { kind: "phrase", phrase: DOORBELL_INSIDE, patch: "softKeys" },
    },
    {
      id: "cj-listen",
      type: "listen",
      prompt:
        "Both start the same: the 5 chord leaning, doorbell glowing " +
        "inside it. One of them answers the door. Which one lets you " +
        "breathe out?",
      options: [
        { id: "a", label: "A", phrase: FIVE_RESOLVES },
        { id: "b", label: "B", phrase: FIVE_STALLS },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "I'm still holding my breath" },
      ],
      responses: {
        a:
          "That's the door opening — the 5 leans, then the doorbell note " +
          "steps up into home and your shoulders drop. That lean-and-open " +
          "is the strongest move in harmony, and now you know exactly why " +
          "it works: the 7 from Unit 1 lives inside the 5 chord, pulling " +
          "the whole thing home.",
        b:
          "B is the one that never answers — the 5 just keeps ringing, " +
          "doorbell going, nobody home. If you exhaled there it may have " +
          "been surrender! Play A once more: same chord, but then the " +
          "doorbell note steps up into home and the door actually opens. " +
          "That pull is the 5 chord's whole job.",
        unsure:
          "Then listen for one note: inside that chord is the doorbell — " +
          "7, your old friend — wanting to take the smallest step up. In " +
          "one version it gets to; in the other it hangs forever. Your " +
          "breath will find it before your brain does.",
      },
    },
    {
      id: "cj-echo",
      type: "echo",
      prompt:
        "Ride the pull with your own hands: the doorbell stepping up into " +
        "home. Smallest step in the neighborhood, biggest relief.",
      calls: [CALL_PULL_STEP, CALL_PULL_RIDE],
    },
    {
      id: "cj-keeper",
      type: "build",
      variant: "progression",
      config: {
        slots: 2,
        pin: { 1: 1 },
        prompt:
          "Two slots, and the second is already home — the landing is " +
          "guaranteed. Your job is the first slot: pick something that " +
          "pulls, so the landing feels earned.",
      },
      checks: [
        {
          id: "has-tension",
          check: "progHasTension",
          args: [],
          message:
            "Nothing's tugging yet — this keeper wants the lean before " +
            "the landing. Try the 5 or the 7 up front, or keep the gentle " +
            "version if gentle is your mood.",
        },
      ],
      keeperId: "tensionRelease",
      keeperName: "your tension & release",
    },
    {
      id: "cj-outro",
      type: "explain",
      body:
        "Tension, then home — you just bottled the move every chorus " +
        "leans on. Next: putting whole trips on repeat, and the four-chord " +
        "engine that runs half the songs you know.",
    },
  ],

  /* ---- 4.4 Loops & Journeys ---- */
  "loops-journeys": [
    {
      id: "lj-famous",
      type: "explain",
      body:
        "Somewhere right now, a thousand songs are running on the same " +
        "four chords. Not because writers are lazy — because these four " +
        "make an engine. Here they are, once around.",
      demo: { kind: "phrase", phrase: LOOP_ONCE, patch: "softKeys" },
    },
    {
      id: "lj-listen",
      type: "listen",
      prompt:
        "Two trips. One pulls into the driveway and switches the engine " +
        "off; the other could circle the block all night. Which one lands?",
      options: [
        { id: "a", label: "A", phrase: LOOP_CYCLING },
        { id: "b", label: "B", phrase: JOURNEY_LANDS },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "neither ever seems to stop" },
      ],
      responses: {
        a:
          "A is the all-night circler — it swings past home in the middle " +
          "of the lap and parks the tension at the seam, so it hands " +
          "itself back to its own beginning instead of stopping. The " +
          "lander is B: it saves home for last and switches off. Neither " +
          "is better — loops are engines, journeys are stories.",
        b:
          "B lands — it saves home for last, touches down on it, and lets " +
          "the engine tick as it cools. A never does: home hides in the " +
          "middle of its lap and the tension sits right at the seam, " +
          "shoving it around again. That's the whole loop trick — and " +
          "thousands of songs run on exactly that engine.",
        unsure:
          "Play them again and listen to what the silence afterward feels " +
          "like: after one, silence feels right; after the other, it " +
          "feels like the record skipped. The record-skip one is the loop " +
          "— it was built to go around again.",
      },
      note: "Loops and journeys are both valid trips. Your keeper can be either.",
    },
    {
      id: "lj-engine",
      type: "explain",
      body:
        "The loop's secret: it hides home in the middle and parks the " +
        "tension at the end. Land on home last and the trip is over — " +
        "that's a journey. Tuck home inside and end leaning, and the loop " +
        "hands itself back to its own beginning, forever. Journeys tell " +
        "stories; loops cast spells.",
    },
    {
      id: "lj-echo",
      type: "echo",
      prompt:
        "Walk each trip's ground floor — the bottom note of every chord, " +
        "one at a time. (You're secretly previewing basslines. Don't tell " +
        "Unit 7.)",
      calls: [CALL_LOOP_ROOTS, CALL_JOURNEY_ROOTS],
    },
    {
      id: "lj-shortlist",
      type: "tinker",
      variant: "chordTour",
      config: {
        mode: "colors",
        prompt:
          "Before you build: one more wander through the palette. " +
          "Shortlist three or four chords that feel like yours — jobs in " +
          "mind, ears in charge.",
      },
    },
    {
      id: "lj-keeper",
      type: "build",
      variant: "progression",
      config: {
        slots: 4,
        prompt:
          "The centerpiece. Four slots: leave home, touch home somewhere, " +
          "keep one chord tugging. Loop it until it feels inevitable — " +
          "like it always existed and you just found it.",
      },
      checks: [
        { id: "leaves-home", check: "progLeavesHome", args: [] },
        { id: "comes-home", check: "progComesHome", args: [] },
        { id: "has-tension", check: "progHasTension", args: [] },
        { id: "loopable-length", check: "progLen", args: [3, 4] },
      ],
      keeperId: "chordLoop",
      keeperName: "your chord loop",
    },
    {
      id: "lj-outro",
      type: "explain",
      body:
        "That loop is your track's engine now — everything from here " +
        "rides on top of it. Next unit: your melodies learn to shake " +
        "hands with your chords.",
      demo: { kind: "track", keeperId: "chordLoop", label: "hear your loop again" },
    },
  ],

  /* ---- 5.1 Safe Notes, Spicy Notes ---- */
  "chord-tones": [
    {
      id: "tone-two-worlds",
      type: "explain",
      body:
        "Your track has melodies, and now it has chords — time they met " +
        "properly. When a chord hums and a melody walks over it, every " +
        "melody note either lives inside the chord or outside it. Inside " +
        "notes melt into the hum; outside notes sparkle against it.",
      demo: { kind: "phrase", phrase: MELODY_MELTS, patch: "softKeys" },
    },
    {
      id: "tone-listen",
      type: "listen",
      prompt:
        "The same chord hums under both melodies. One melody sinks into " +
        "it like it lives there; the other keeps elbowing it. Which one " +
        "melts?",
      options: [
        { id: "a", label: "A", phrase: MELODY_MELTS },
        { id: "b", label: "B", phrase: MELODY_FIGHTS },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "they both just float by" },
      ],
      responses: {
        a:
          "That's the melt — every note of A lives inside the humming " +
          "chord, so melody and harmony fuse into one warm thing. B never " +
          "touches a chord note: constant elbows. Neither is wrong — but " +
          "now you can hear the dial, and soon you'll set it on purpose.",
        b:
          "B is the elbower — its notes all live outside the chord, so " +
          "every one rubs. If that rub pulled you in, good instinct: it's " +
          "spice, and melodies need some. But the melting one is A, every " +
          "note tucked inside the chord. The craft is choosing when to " +
          "melt and when to rub.",
        unsure:
          "Try it with your shoulders instead of your ears: A should let " +
          "them drop; B should keep them slightly up. The one you could " +
          "fall asleep inside is the melt.",
      },
    },
    {
      id: "tone-tinker",
      type: "tinker",
      variant: "chordTones",
      config: {
        phrase: GLUE_MIX,
        chordDegree: 1,
        prompt:
          "This melody mixes both kinds. Flip on the chord's notes and " +
          "tap around: the warm lanes belong to the chord — hear which " +
          "melody notes melt into it and which ones sparkle against it.",
      },
    },
    {
      id: "tone-names",
      type: "explain",
      body:
        "Names, now that you've heard the difference: notes from the " +
        "chord are chord tones — the glue, safe as houses. The others are " +
        "color notes — the spice. All glue is sturdy but plain; all spice " +
        "burns. Melodies live in the mix, and they land on the glue.",
    },
    {
      id: "tone-keeper",
      type: "try",
      instructions:
        "The 1 chord hums for you. Record a little line over it — three " +
        "notes or more. Lean on the notes that melt, sprinkle sparkle " +
        "where it wants sparkle, and notice what your ear asks of the " +
        "last note.",
      bed: { chordDegree: 1 },
      checks: [{ id: "enough-notes", check: "minNotes", args: [3] }],
      keeperId: "glueNotes",
      keeperName: "the glue notes",
      withDrone: true,
    },
    {
      id: "tone-outro",
      type: "explain",
      body:
        "Melt and sparkle — that's the whole vocabulary of melody over " +
        "chords. Next: aiming. Landing on the glue on purpose is what " +
        "makes a line sound like it knows where it's going.",
    },
  ],

  /* ---- 5.2 Targets & Passing Notes ---- */
  "targets-passing": [
    {
      id: "tp-aim",
      type: "explain",
      body:
        "A strong line doesn't just avoid clashes — it aims. Pick a note " +
        "the chord owns, then walk to it, letting the in-between notes " +
        "stream past like scenery. Chord tones are the stepping stones; " +
        "everything between is the stride.",
      demo: { kind: "phrase", phrase: WALK_SOLID, patch: "softKeys" },
    },
    {
      id: "tp-listen",
      type: "listen",
      prompt:
        "Both walk the same road over the same chord. One arrives on a " +
        "stone that belongs to the chord; the other stops in the gap " +
        "between stones. Which arrival feels solid?",
      options: [
        { id: "a", label: "A", phrase: WALK_SOLID },
        { id: "b", label: "B", phrase: WALK_BETWEEN },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "both arrivals feel the same" },
      ],
      responses: {
        a:
          "Solid as it gets — A's last step lands on a note the chord " +
          "owns, so the ground pushes back. B stops one note shy, " +
          "hovering in the gap. Landings the chord owns are called " +
          "targets, and aiming at them is what makes a line sound " +
          "intentional.",
        b:
          "B's arrival hangs — it stops between the stones, on a note the " +
          "chord doesn't own, and you can feel the ground not quite " +
          "arrive. If you liked that hover, remember it: it's a real " +
          "color. But the solid landing is A, square on a chord tone. " +
          "That's a target.",
        unsure:
          "One more pass, ears on the final note only: after one, you " +
          "could set the phrase down and walk away; after the other, your " +
          "ear keeps reaching. Solid ground is the one you can walk away " +
          "from.",
      },
    },
    {
      id: "tp-names",
      type: "explain",
      body:
        "So: targets are the chord tones you land on — endings, big " +
        "beats, anywhere the ear checks in. Passing notes are the scale " +
        "steps that carry you between targets. Held alone they'd rub, but " +
        "kept walking they're what smoothness is made of.",
    },
    {
      id: "tp-tinker",
      type: "tinker",
      variant: "chordTones",
      config: {
        phrase: WALK_ROUND,
        chordDegree: 1,
        prompt:
          "The same walk, up and back. Flip the chord's notes on: the " +
          "walk touches down on a stone every few steps — everything " +
          "between is stride. Tap any note against the hum and hear which " +
          "is which.",
      },
    },
    {
      id: "tp-echo",
      type: "echo",
      prompt:
        "Echo these — each one walks its own road, but every one lands on " +
        "a stone the 1 chord owns. Feel the ground arrive under the last " +
        "note.",
      calls: [CALL_LAND_G, CALL_LAND_B, CALL_LAND_D],
    },
    {
      id: "tp-keeper",
      type: "try",
      instructions:
        "The chord hums. Pick two of its notes as your targets and walk " +
        "between them — mostly steps, one leap at most, four notes or " +
        "more. Land where the ground is.",
      bed: { chordDegree: 1 },
      checks: [
        { id: "enough-notes", check: "minNotes", args: [4] },
        {
          id: "mostly-steps",
          check: "maxLeaps",
          args: [1],
          message:
            "That line vaults more than it walks. Targets feel aimed-at " +
            "when you stride up to them — try at most one leap, or keep " +
            "the parkour if it's saying what you mean.",
        },
      ],
      keeperId: "smoothLine",
      keeperName: "the smooth line",
      withDrone: true,
    },
    {
      id: "tp-outro",
      type: "explain",
      body:
        "Aim, walk, land — your lines know where they're going now. One " +
        "lesson left in this unit: your own melody meets your own chords, " +
        "and the track gets its chorus.",
    },
  ],

  /* ---- 5.3 The Rewrite ---- */
  "the-rewrite": [
    {
      id: "rw-remember",
      type: "explain",
      body:
        "Back in Melody Shapes you wrote a real melody — question, " +
        "answer, landing — over nothing but a drone. Listen to it again. " +
        "It's about to get a whole world underneath.",
      demo: {
        kind: "track",
        keeperId: "melodyA",
        patch: "softKeys",
        drone: true,
        label: "hear your melody, part A",
      },
    },
    {
      id: "rw-moving-floor",
      type: "explain",
      body:
        "A drone is a floor that never moves. A chord loop is a floor " +
        "that moves every bar — so your landings matter more now: each " +
        "passing chord catches different notes. Here's a melody aiming a " +
        "landing at each chord of the famous loop as it goes by.",
      demo: { kind: "phrase", phrase: REWRITE_EXAMPLE, patch: "softKeys" },
    },
    {
      id: "rw-listen",
      type: "listen",
      prompt:
        "Same chords, same melody — until the very last note. One landing " +
        "drops into the home chord like a ball into a glove. Which one " +
        "gets caught?",
      options: [
        { id: "a", label: "A", phrase: LANDING_CAUGHT },
        { id: "b", label: "B", phrase: LANDING_SLIPS },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "both landings blur past me" },
      ],
      responses: {
        a:
          "Caught — A's last note is one the home chord owns, so the " +
          "chord closes around it and the whole thing clicks shut. B " +
          "lands one step off the glove and hangs there. Aim your last " +
          "note where the chord is, and the harmony does half your " +
          "landing for you.",
        b:
          "B is the one that slips — its last note isn't in the waiting " +
          "chord, so it hangs just off the glove. Lovely tension, if you " +
          "mean it! But the caught one is A: same line, last note tucked " +
          "inside the home chord. When melody and chord agree on a " +
          "landing, you barely have to try.",
        unsure:
          "One more spin, ears on the final beat only: the chord and the " +
          "note either shake hands or just miss. The handshake has a " +
          "little click of done in it — that's the catch.",
      },
    },
    {
      id: "rw-ride",
      type: "explain",
      body:
        "The rewrite, then: your own loop plays underneath, and you ride " +
        "it. You already know everything you need — steps and one " +
        "well-spent leap, a shape with a summit, say-it-again, landings " +
        "on the glue. Steal from your part A melody or start fresh; " +
        "either way, aim the last note at home just as your loop swings " +
        "by it.",
    },
    {
      id: "rw-keeper",
      type: "try",
      instructions:
        "Your chord loop plays four times — that's your runway. Record " +
        "your melody over it: six notes or more, and land on home at the " +
        "end. This is the rewrite: your melody, over your chords.",
      bed: { keeperId: "chordLoop", times: 4 },
      checks: [
        { id: "enough-notes", check: "minNotes", args: [6] },
        {
          id: "ends-home",
          check: "endsOnDegree",
          args: [1],
          message:
            "The loop came home and your melody kept flying — dramatic! " +
            "This keeper wants the two of you landing together on 1. One " +
            "more pass? Or keep the flyover if you love it.",
        },
      ],
      keeperId: "trackMelody",
      keeperName: "your melody, over your chords",
      withDrone: false,
    },
    {
      id: "rw-outro",
      type: "explain",
      body:
        "That's the sound of a track becoming a song: your loop and your " +
        "melody, written by the same ear, landing in the same place. Head " +
        "to the Studio and press play — it now stacks your chord loop " +
        "under this melody. Your first real chorus. Next unit: the same " +
        "neighborhood at dusk — the minor side.",
      demo: {
        kind: "track",
        keeperId: "trackMelody",
        drone: true,
        label: "hear the rewrite once more",
      },
    },
  ],

  /* ---- 6.1 Same Notes, New Home ---- */
  "new-home": [
    {
      id: "nh-tilt",
      type: "explain",
      body:
        "Your track knows one home: G, sunlight, the note everything falls " +
        "back to. Here's this unit's secret — those same seven notes have a " +
        "second home hiding inside them, three steps down. Same " +
        "neighborhood, different front door. The notes don't change; the " +
        "gravity does.",
      demo: { kind: "phrase", phrase: DUSK_WANDER, patch: "softKeys" },
    },
    {
      id: "nh-two-houses",
      type: "listen",
      prompt:
        "The very same phrase, twice — note for note identical. Only the " +
        "hum underneath moves. Same notes: whose house are we in? In which " +
        "one does the last note feel like coming home?",
      options: [
        { id: "a", label: "A", phrase: HOUSE_OF_G },
        { id: "b", label: "B", phrase: HOUSE_OF_E },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "the houses look the same to me" },
      ],
      responses: {
        a:
          "Interesting — in A the hum is G, so that last note hangs a " +
          "little way above the floor: the wistful 6, hovering. Some ears " +
          "love the hover so much it reads as arrival. Most ears land in " +
          "B: the very same note, but the hum has moved to E, so the " +
          "melody ends exactly where the floor is. Play them again and " +
          "feel the last note change jobs.",
        b:
          "That's the tilt — the melody never moved; the floor did. In A " +
          "the hum is G and that final note floats above it, wistful. In " +
          "B the hum is E and the same note lands square on the ground: " +
          "home. Same seven notes, brand-new gravity.",
        unsure:
          "Honestly, perfect instinct — the notes ARE the same, every " +
          "single one. Play them once more and listen only to the very " +
          "last note against the hum: in one it hovers a little above the " +
          "floor; in the other it IS the floor. That difference is this " +
          "entire unit.",
      },
      note: "Not one note of the melody changed — only the floor under it.",
    },
    {
      id: "nh-relative",
      type: "explain",
      body:
        "The second home is E — the note you've known as 6, the wistful " +
        "one, promoted. Hum E underneath and the whole neighborhood " +
        "renumbers itself: E becomes 1, and everything you know — degrees, " +
        "jobs, loops — comes along with new numbers. Even the old home G " +
        "is still here, wearing a 3 now: the sunny cousin who visits. " +
        "Musicians call this the relative minor. We'll call it the shadow " +
        "home.",
    },
    {
      id: "nh-gravity",
      type: "tinker",
      variant: "gravity",
      config: {
        home: "minor",
        prompt:
          "The drone hums on E now. Play the notes you already know and " +
          "feel how the pull has moved house — G rests easy like a guest, " +
          "and it's E that everything wants to fall back to.",
      },
    },
    {
      id: "nh-echo",
      type: "echo",
      prompt:
        "Echo each little fall. They all settle on E — let your hands " +
        "learn where the new floor is.",
      calls: [CALL_DUSK_STEP, CALL_DUSK_SIGH, CALL_DUSK_HIGH, CALL_DUSK_WALK],
    },
    {
      id: "nh-keeper",
      type: "try",
      instructions:
        "Wander the same seven notes over the E drone — one rule, and you " +
        "know it already: end on the new home and let it ring.",
      droneMidi: 40,
      checks: [
        { id: "enough-notes", check: "minNotes", args: [3] },
        { id: "ends-shadow-home", check: "endsOnMinorDegree", args: [1] },
      ],
      keeperId: "minorHomecoming",
      keeperName: "the shadow homecoming",
      withDrone: true,
    },
    {
      id: "nh-outro",
      type: "explain",
      body:
        "Your track just found its shadow side's first phrase. Next door: " +
        "the chords renumber themselves too — and you'll discover the one " +
        "thing the minor side is missing. The doorbell broke in the move. " +
        "We're going to borrow a new one.",
    },
  ],

  /* ---- 6.2 Minor Progressions ---- */
  "minor-moves": [
    {
      id: "mm-transfer",
      type: "explain",
      body:
        "Everything transfers. The stacks you built in the major " +
        "neighborhood are all still standing — same voicings, same fingers " +
        "— they've just renumbered themselves around the shadow home. E's " +
        "own stack is the 1 chord now. Here's the whole ladder, climbing " +
        "from the new home back to the new home.",
      demo: { kind: "phrase", phrase: MINOR_LADDER, patch: "softKeys" },
    },
    {
      id: "mm-colors",
      type: "tinker",
      variant: "chordTour",
      config: {
        mode: "minor",
        tourMode: "colors",
        prompt:
          "The seven chords, at dusk. Tap through and meet the new " +
          "house-names — old friends in new light. Notice who moved into " +
          "the 1, and where the sunny cousin ended up.",
      },
    },
    {
      id: "mm-bell-listen",
      type: "listen",
      prompt:
        "Two little trips: home, the 5 chord leaning, home again. In one " +
        "of them the old doorbell pull is back — that itch that has to be " +
        "answered. Which one rings the bell?",
      options: [
        { id: "a", label: "A", phrase: BELL_NATURAL },
        { id: "b", label: "B", phrase: BELL_BORROWED },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "no bells either way" },
      ],
      responses: {
        a:
          "A is the softer lean — its 5 chord keeps the neighborhood's own " +
          "D, and D doesn't ache to go anywhere, so coming home feels " +
          "gentle, almost optional. The bell lives in B: one note raised a " +
          "half step, and suddenly the chord leans on home's door the way " +
          "the old 5 did in sunlight. Play them again and listen for the " +
          "note that climbs.",
        b:
          "That's the bell — and it cost exactly one note. B's 5 chord " +
          "raises its middle from D to D sharp, a single half step, and " +
          "that raised note sits so close under home it aches to climb in. " +
          "A keeps the neighborhood's own D and just… drifts back. " +
          "Softness has its uses — but when you want the door answered, " +
          "you borrow the sharp.",
        unsure:
          "They're one note apart, so fair. Play both again and hold your " +
          "breath on the middle chord: after one of them, coming home " +
          "feels optional; after the other, inevitable. The inevitable one " +
          "has the bell.",
      },
    },
    {
      id: "mm-borrowed",
      type: "explain",
      body:
        "Meet the minor side's one genuinely new note. The natural 5 " +
        "chord is soft because its middle note, D, doesn't lean anywhere. " +
        "Raise that D a half step — to D sharp, a note from outside the " +
        "neighborhood — and the doorbell is rebuilt: D sharp sits one tiny " +
        "step under home and aches to step up into it, exactly the way 7 " +
        "pulled to 1 in the sunlight. That chord — the 5 with its heart " +
        "raised — is the borrowed doorbell, and it's what makes minor " +
        "progressions pull.",
    },
    {
      id: "mm-jobs",
      type: "tinker",
      variant: "chordTour",
      config: {
        mode: "minor",
        tourMode: "jobs",
        raisedFive: true,
        prompt:
          "Same three jobs as ever — home, away, tension. Tap each chord, " +
          "file it by feel before it tells you. The new 5♯ chip is the " +
          "borrowed doorbell; guess its job before you peek.",
      },
    },
    {
      id: "mm-keeper",
      type: "build",
      variant: "progression",
      config: {
        chordMode: "minor",
        raisedFive: true,
        slots: 4,
        prompt:
          "The shadow side's engine. Four slots: leave the shadow home, " +
          "touch it somewhere, keep something tugging. The borrowed " +
          "doorbell is on the shelf if you want the strongest pull in the " +
          "house.",
      },
      checks: [
        { id: "leaves-home", check: "progLeavesHome", args: [] },
        { id: "comes-home", check: "progComesHome", args: [] },
        { id: "has-tension", check: "progHasTension", args: [] },
        { id: "loopable-length", check: "progLen", args: [3, 4] },
      ],
      keeperId: "minorLoop",
      keeperName: "your minor loop",
    },
    {
      id: "mm-outro",
      type: "explain",
      body:
        "A loop of your own on the minor side — the track's shadow engine, " +
        "idling and ready. Next door we put it to work: your song learns " +
        "to change the weather on purpose, and your track gets its B " +
        "section.",
    },
  ],

  /* ---- 6.3 The Mood Switch ---- */
  "mood-switch": [
    {
      id: "ms-a-side",
      type: "explain",
      body:
        "Listen to your track's engine again — your chord loop, the sound " +
        "of the A side. It's been running in sunlight since the day you " +
        "built it. Beautiful — and if a song only ever has one weather, " +
        "the ear stops feeling it. Sunlight you never leave is just light.",
      demo: { kind: "track", keeperId: "chordLoop", label: "hear your A side" },
    },
    {
      id: "ms-weather-listen",
      type: "listen",
      prompt:
        "Two loops take the exact same trip — leave home, wander, end on " +
        "the lean. One drives it through the afternoon; the other drives " +
        "it at dusk. Which one is the dusk?",
      options: [
        { id: "a", label: "A", phrase: TRIP_AFTERNOON },
        { id: "b", label: "B", phrase: TRIP_DUSK },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "the light won't settle for me" },
      ],
      responses: {
        a:
          "A is the afternoon — the major side you've lived on for five " +
          "units, so its wandering feels like ordinary daylight. The dusk " +
          "is B: the identical trip renumbered around the shadow home, " +
          "with the borrowed doorbell doing the leaning at the end. Play " +
          "them back to back once more — same doors, different light.",
        b:
          "That's the dusk — the same trip, renumbered around the shadow " +
          "home, the borrowed doorbell leaning at the end. A is the " +
          "identical journey in afternoon light. Nothing about the trip " +
          "changed but the weather — and that switch is exactly the trick " +
          "your track is about to learn.",
        unsure:
          "Both keep moving, so fair. Try it with your shoulders: one " +
          "loop leaves them where they were; the other pulls them " +
          "somewhere a little tender. The tender one is the dusk.",
      },
      note: "Same trip, two weathers. Songs breathe by switching between them.",
    },
    {
      id: "ms-breathe",
      type: "explain",
      body:
        "That switch has a name in nearly every song you love: the A " +
        "section and the B section. Verse into chorus, chorus into bridge " +
        "— the weather changes so that coming back means something. Your " +
        "track has a sunlit A side; the minor loop you built last door is " +
        "the floor of its B side. All it's missing is a melody walking " +
        "through the dusk.",
    },
    {
      id: "ms-floor",
      type: "explain",
      body:
        "Here's that floor again — your own minor loop, idling. This is " +
        "the runway your B section melody takes off from. Everything you " +
        "know about melody still works at dusk: mostly steps, one clear " +
        "summit, landings on notes the chords own — just aimed, now, at " +
        "the shadow home.",
      demo: { kind: "track", keeperId: "minorLoop", label: "hear your minor loop" },
    },
    {
      id: "ms-keeper",
      type: "try",
      instructions:
        "Your minor loop plays four times — that's the runway. Record the " +
        "B section's melody over it: four notes or more, and land on the " +
        "shadow home at the end so both sides of your track know where " +
        "they live.",
      bed: { keeperId: "minorLoop", times: 4 },
      droneMidi: 40,
      checks: [
        { id: "enough-notes", check: "minNotes", args: [4] },
        { id: "ends-shadow-home", check: "endsOnMinorDegree", args: [1] },
      ],
      keeperId: "bSection",
      keeperName: "your B section",
      withDrone: false,
    },
    {
      id: "ms-outro",
      type: "explain",
      body:
        "Your track breathes now — sunlight, then dusk, then back. Head " +
        "to the Studio and press “play the B side”: your minor " +
        "loop under this melody, the shadow half of your song, standing " +
        "on its own two feet. Next unit: the floor learns to move — " +
        "groove, and the bass that drives it.",
    },
  ],

  /* ---- 7.1 The Grid ---- */
  "the-grid": [
    {
      id: "tg-time",
      type: "explain",
      body:
        "Everything you've made so far floats in free time — you played, and " +
        "the track breathed with you. Music with a pulse makes a promise " +
        "instead: it divides time into a steady grid and keeps the count. " +
        "Here's the whole idea at its smallest — one note, keeping time.",
      demo: { kind: "phrase", phrase: GRID_PULSE, patch: "softKeys" },
    },
    {
      id: "tg-feel",
      type: "tinker",
      variant: "grid",
      config: {
        pattern: "heartbeat",
        bpm: 92,
        prompt:
          "A bar is 16 little steps, and they aren't equal: steps 1, 5, 9, " +
          "and 13 are the strong ones — the floors. Start the groove, then " +
          "tap steps to sprinkle taps onto them. Landing ON a floor and " +
          "landing BETWEEN floors are two different feelings. Collect both.",
      },
    },
    {
      id: "tg-strong-weak",
      type: "explain",
      body:
        "So time in music is a grid of strong and weak. Beats 1 and 3 are " +
        "the floors — they hold the weight. Beats 2 and 4 are the answer. " +
        "And the cracks between beats are where all the funk lives. Nothing " +
        "you've learned went away, by the way: a groove is just tension and " +
        "release happening in time.",
    },
    {
      id: "tg-swing",
      type: "listen",
      prompt:
        "The same little riff, twice — note for note the same. In one, " +
        "every step lands exactly on the grid. In the other, the in-between " +
        "notes lean late, like they're dragging their heels. Which one " +
        "leans?",
      options: [
        { id: "a", label: "A", phrase: RIFF_STRAIGHT },
        { id: "b", label: "B", phrase: RIFF_SWUNG },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "they walk the same to me" },
      ],
      responses: {
        a:
          "A is the ruler-straight one — every note exactly where the grid " +
          "says. The leaner is B: same notes, but every in-between step " +
          "arrives a touch late. That lean is called swing, and it isn't " +
          "sloppiness — it's the weak steps flirting with the strong ones. " +
          "Play them again and let your shoulders pick a favorite.",
        b:
          "That's the lean — B drags every in-between note just a little " +
          "late, and the riff starts to slouch in the best way. That's " +
          "swing: not one note changes, only when the weak ones arrive. A " +
          "is the same riff walking perfectly straight. Neither is righter " +
          "— they're two kinds of walking.",
        unsure:
          "Fair — swing hides in milliseconds, on purpose. Play them once " +
          "more and nod along: one of them nods exactly with you, and one " +
          "keeps arriving a hair after your nod. The late one is the swing.",
      },
      note: "Straight and swung are both true. Lo-fi leans; synthwave doesn't. You'll choose soon.",
    },
    {
      id: "tg-keeper",
      type: "build",
      variant: "drums",
      config: {
        rows: ["kick"],
        bpm: 92,
        prompt:
          "One row: the kick — your track's heartbeat. Tap in where it " +
          "lands, loop it, and edit while it plays. Where does the floor of " +
          "YOUR groove live? (Most floors start on step 1.)",
      },
      checks: [{ id: "kick-on-one", check: "beatHasKickOnOne", args: [] }],
      keeperId: "pulse",
      keeperName: "your pulse",
    },
    {
      id: "tg-outro",
      type: "explain",
      body:
        "Your track has a pulse now — the floor everything else will stand " +
        "on. Next door, the kick hires its two best friends: a snare to " +
        "answer it, and a hat to carry the time between.",
    },
  ],

  /* ---- 7.2 Kick, Snare, Hat ---- */
  drums: [
    {
      id: "dr-kit",
      type: "explain",
      body:
        "Meet the kit — three sounds, infinite grooves. The kick is the " +
        "floor: low and round, the beat you feel in your chest. The snare " +
        "is the answer: a clap that talks back on 2 and 4. The hat is the " +
        "clock: small and bright, ticking the time between them. Every beat " +
        "you've ever nodded to is mostly these three taking turns.",
    },
    {
      id: "dr-listen",
      type: "listen",
      prompt:
        "Two pulses built from one chord. One stamps on every single beat " +
        "— a march, all floors. The other skips the floors entirely and " +
        "lands only on the answering beats, 2 and 4. Which one is the clap?",
      options: [
        { id: "a", label: "A", phrase: STAB_CLAP },
        { id: "b", label: "B", phrase: STAB_MARCH },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "my hands can't decide" },
      ],
      responses: {
        a:
          "That's the clap — A skips beats 1 and 3 and lands square on 2 " +
          "and 4, the answering beats. That placement is the backbeat, and " +
          "it's the oldest engine in pop: the floor falls, the clap " +
          "answers. B is the march — a stamp on every beat, four on the " +
          "floor — and dance music runs on exactly that relentlessness.",
        b:
          "B is the march — a stamp on every single beat, no gaps, no " +
          "answer. Four on the floor, dance music's engine, and if it " +
          "pulled you, relentless is a real power. The clap is A: it skips " +
          "the floors and lands only on 2 and 4 — the backbeat, the clap " +
          "your hands already know from every song you've clapped along to.",
        unsure:
          "Then clap along with each one — hands, not ears. With one of " +
          "them your claps fall into the gaps between its hits; with the " +
          "other you're clapping exactly where it lands. The one your " +
          "hands agree with on 2 and 4 is the backbeat.",
      },
      note: "Backbeat and four-on-the-floor aren't rivals — they're two engines. Your beat can run either.",
    },
    {
      id: "dr-conversation",
      type: "explain",
      body:
        "So the classic recipe: kick on the floors, snare answering on 2 " +
        "and 4, hats ticking the time between — a conversation in three " +
        "registers. And keep one thing in your pocket for next door: " +
        "whatever your kick does, the bass is going to want to do it too.",
    },
    {
      id: "dr-echo",
      type: "echo",
      prompt:
        "Your piano is a drum kit for a minute — low keys are the kick, " +
        "the middle is the snare, up high is the hat. Echo the rhythm of " +
        "each call back; the exact pitch barely matters here.",
      calls: [CALL_FLOORS, CALL_CLAPS, CALL_TICKS],
    },
    {
      id: "dr-keeper",
      type: "build",
      variant: "drums",
      config: {
        startFrom: "heartbeat",
        bpm: 92,
        prompt:
          "Your beat. The heartbeat kick is already in — grow it: keep a " +
          "kick on step 1, give the snare 2 and 4 if it wants them, let " +
          "the hats carry the time. Loop it and edit while it plays; the " +
          "grid is live.",
      },
      checks: [
        { id: "kick-on-one", check: "beatHasKickOnOne", args: [] },
        { id: "backbeat", check: "beatHasBackbeat", args: [] },
        { id: "alive", check: "beatNotEmpty", args: [6] },
      ],
      keeperId: "beat",
      keeperName: "your beat",
    },
    {
      id: "dr-outro",
      type: "explain",
      body:
        "That's your track's drummer hired. One player is still missing " +
        "from the low end — the one who glues your chords to your kick. " +
        "Next: the bass.",
    },
  ],

  /* ---- 7.3 Roots in the Pocket ---- */
  bass: [
    {
      id: "bs-handshake",
      type: "explain",
      body:
        "The bass is where harmony and rhythm shake hands. It plays notes, " +
        "like your melody — but it plays them where the kick plays, like " +
        "your drums. One line, both jobs. Here's one pacing its room: low, " +
        "patient, never in a hurry.",
      demo: { kind: "phrase", phrase: BASS_PACE, patch: "softKeys" },
    },
    {
      id: "bs-listen",
      type: "listen",
      prompt:
        "The same four chords pass overhead twice, a bassline walking " +
        "under each. One bass plants itself on the root of every chord as " +
        "it arrives — the note the chord is named after. The other wanders " +
        "wherever it likes. Which one holds the building up?",
      options: [
        { id: "a", label: "A", phrase: BASS_ROOTED },
        { id: "b", label: "B", phrase: BASS_WANDER },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "both floors feel fine" },
      ],
      responses: {
        a:
          "That's the one holding the building up — every time a chord " +
          "arrives, A's bass is already standing on its root, the note the " +
          "chord is named after. Roots follow chords: the first law of " +
          "bass. B wanders — pretty notes, but the floor keeps sliding out " +
          "from under the chords, and you can feel the building sway.",
        b:
          "B has charm — a wandering bass is a real color, and some songs " +
          "want that sway. But listen to the chords almost tipping over: " +
          "the wanderer never stands on the note each chord is named " +
          "after. A does — it plants on every root the moment the chord " +
          "lands, and the whole building sits square. Roots follow chords: " +
          "the first law of bass.",
        unsure:
          "Listen only to the moments the chord changes: at every arrival, " +
          "one bass is standing exactly under the new chord like a " +
          "foundation, and one is somewhere off to the side. The " +
          "foundation feeling is the roots.",
      },
      note: "Wandering isn't wrong — it's seasoning. Roots are the meal.",
    },
    {
      id: "bs-two-laws",
      type: "explain",
      body:
        "Two laws, and you already know both halves. Law one, from " +
        "harmony: roots follow chords — when the chord changes, the bass " +
        "lands on its root. Law two, from groove: lock to the kick — put " +
        "your bass notes where your kick lives, and the two fuse into one " +
        "instrument, the low end. Break either law whenever you like. Just " +
        "break it on purpose.",
    },
    {
      id: "bs-echo",
      type: "echo",
      prompt:
        "Bass hands: echo each call down in the low end. One pumps a " +
        "single root, one walks up to the next chord's door, one walks " +
        "back down to home.",
      calls: [CALL_ROOT_PUMP, CALL_ROOT_WALK, CALL_ROOT_DROP],
    },
    {
      id: "bs-keeper",
      type: "try",
      instructions:
        "Your bassline. Your chord loop plays four times with your beat " +
        "underneath, and the keys have dropped an octave into bass " +
        "country. Ride the roots, lock to the kick, and walk between " +
        "chords when the walk feels earned — four notes or more, two " +
        "leaps at most.",
      bed: { keeperId: "chordLoop", times: 4, withBeat: true },
      recordPatch: "bassDrop",
      octaveShift: -12,
      checks: [
        { id: "enough-notes", check: "minNotes", args: [4] },
        {
          id: "walks-not-jumps",
          check: "maxLeaps",
          args: [2],
          message:
            "Bass players walk more than they jump — big leaps down low " +
            "get seasick fast. Try at most two, or keep the kangaroo line " +
            "if it grooves.",
        },
      ],
      keeperId: "bassline",
      keeperName: "your bassline",
      withDrone: false,
    },
    {
      id: "bs-outro",
      type: "explain",
      body:
        "Low end online: chords, kick, and bass all agreeing about where " +
        "the ground is. Your track is a band now. Next unit: deciding who " +
        "plays when — because a band that all plays at once, always, is " +
        "just weather.",
    },
  ],

  /* ---- 8.1 Layers & Space ---- */
  "roles-space": [
    {
      id: "rs-lanes",
      type: "explain",
      body:
        "Count your band: drums, bass, chords, melody, a drone. The " +
        "fastest way to ruin five good parts is to let them all shout in " +
        "the same place at once. Arranging starts with lanes: every part " +
        "owns a range — bass in the basement, chords on the ground floor, " +
        "melody upstairs — and its own amount of motion. Parts that own " +
        "their lane never have to fight.",
    },
    {
      id: "rs-listen",
      type: "listen",
      prompt:
        "Two little mixes, both playing the same chords with company. In " +
        "one, every part crowds into the same middle floor, elbow to " +
        "elbow. In the other, the parts spread out — basement, ground " +
        "floor, upstairs. In which one can you actually follow each part?",
      options: [
        { id: "a", label: "A", phrase: MIX_CLUTTER },
        { id: "b", label: "B", phrase: MIX_SPACED },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "I lose the parts in both" },
      ],
      responses: {
        a:
          "Sharp ears if you followed anything in there — A is the " +
          "crowded one: three parts jammed into the same middle octave, " +
          "every line stepping on its neighbors' toes. Most ears find the " +
          "parts in B, where each one keeps its own floor: bass low, " +
          "chords mid, melody high. Same chords — the space is what " +
          "changed.",
        b:
          "That's the spread — B gives every part its own floor: the bass " +
          "in the basement, chords in the middle, the melody up in the " +
          "light. Nobody overlaps, so your ear can walk from part to part " +
          "like rooms in a house. A crams the same music into one octave " +
          "and it turns to fog. Space isn't emptiness — it's legibility.",
        unsure:
          "Then try grabbing just one line and humming along with it " +
          "through each mix. In one, your hum keeps getting bumped off " +
          "course by a neighbor; in the other, your part has the whole " +
          "floor to itself. The easy hum is the spaced mix.",
      },
      note: "Clutter isn't evil — shoegaze bathes in it. But choose it; don't default to it.",
    },
    {
      id: "rs-time-lane",
      type: "explain",
      body:
        "Range is one lane; time is the other. Parts can take turns — " +
        "melody speaks, chords answer; the drone holds while everyone else " +
        "breathes. And the most underrated move in arranging is the rest: " +
        "a part that sometimes doesn't play. Silence is a layer you can " +
        "place, same as any other.",
    },
    {
      id: "rs-echo",
      type: "echo",
      prompt:
        "Feel the floors under your fingers: one call lives in the " +
        "basement, one lives upstairs. Echo each in its own lane and " +
        "notice how different the same kind of line feels at each " +
        "altitude.",
      calls: [CALL_LOW_LANE, CALL_HIGH_LANE],
    },
    {
      id: "rs-keeper",
      type: "build",
      variant: "layers",
      config: {
        stages: ["A", "B"],
        stageBars: 2,
        prompt:
          "Your track, two stretches: A and B. Every cell is a decision — " +
          "who plays in this stretch? Carve at least one difference " +
          "between the columns so the song changes rooms, then play the " +
          "stages and listen to the doors open.",
      },
      checks: [{ id: "stages-differ", check: "stagesVary", args: [] }],
      keeperId: "layerMap",
      keeperName: "the layer map",
    },
    {
      id: "rs-outro",
      type: "explain",
      body:
        "Who plays when — you just made that decision on purpose, which " +
        "is most of what arranging is. Next: pointing the same skill at " +
        "energy, because adding and removing layers is how songs build " +
        "and drop.",
    },
  ],

  /* ---- 8.2 Builds & Drops ---- */
  "builds-drops": [
    {
      id: "bd-energy",
      type: "explain",
      body:
        "Here's the whole physics of it: energy is density. More layers, " +
        "more motion, more notes — the music feels like it's rising. " +
        "Fewer, and it settles. A song's drama isn't in any single bar; " +
        "it's in the shape of how much is happening, bar over bar. You've " +
        "spent seven units on tension and release inside the music. This " +
        "is tension and release as the amount of music.",
    },
    {
      id: "bd-listen",
      type: "listen",
      prompt:
        "Two stretches, same length, same ingredients. One starts with " +
        "almost nothing and keeps adding — another layer, and another, " +
        "until it's full. One plays the same amount the whole way " +
        "through. Which one is climbing somewhere?",
      options: [
        { id: "a", label: "A", phrase: GROW_DENSITY },
        { id: "b", label: "B", phrase: FLAT_DENSITY },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "neither seems to move" },
      ],
      responses: {
        a:
          "That's the climb — A starts with one lonely low note and " +
          "stacks a new layer every bar: chords join, then a melody, then " +
          "everything at once. Each addition is a promise that something " +
          "is coming. B never changes its amount, and after a few bars " +
          "your ear files it under wallpaper. Motion in the mix is a " +
          "melody of its own.",
        b:
          "B is the flatline — the same density the whole way through, " +
          "and steady has real uses: trances are built from it. But the " +
          "climbing one is A: one low note, then chords, then melody, " +
          "each bar fuller than the last. That stacking is a build, and " +
          "it's how songs point at their own future.",
        unsure:
          "Count along, loosely — how MUCH is happening in each bar, not " +
          "which notes. In one, the answer keeps growing: one thing, then " +
          "three, then five. In the other, the answer never moves. The " +
          "growing one is the build.",
      },
      note: "Builds make promises. Next: how songs keep them.",
    },
    {
      id: "bd-drop",
      type: "explain",
      body:
        "A build is a promise, and the payment is the drop: at the top of " +
        "the climb, snatch layers away — sometimes everything but one — " +
        "and the room falls open. It works because of everything you " +
        "already know: tension needs release, and by the top of a build, " +
        "density IS the tension. The emptiness after is the biggest sound " +
        "in the song.",
    },
    {
      id: "bd-echo",
      type: "echo",
      prompt:
        "Play a build with your own hands: three calls, the same idea, " +
        "each one fuller than the last. Echo them in order and feel " +
        "yourself leaning toward the top.",
      calls: [CALL_ARC_SPARSE, CALL_ARC_BUILDING, CALL_ARC_FULL],
    },
    {
      id: "bd-keeper",
      type: "build",
      variant: "layers",
      config: {
        stages: ["sparse", "building", "full", "drop"],
        stageBars: 2,
        prompt:
          "The energy arc: four stages. Start sparse, add toward full, " +
          "then make the drop mean something — cut layers away at exactly " +
          "the moment the ear expects more. (Dropping to almost nothing " +
          "is a classic for a reason.)",
      },
      checks: [{ id: "stages-differ", check: "stagesVary", args: [] }],
      keeperId: "energyArc",
      keeperName: "the energy arc",
    },
    {
      id: "bd-outro",
      type: "explain",
      body:
        "Sparse to full to gone — your track has dynamics now: a shape in " +
        "time, not just a set of parts. Two doors left. The next one is " +
        "pure play: your song tries on three completely different " +
        "outfits.",
    },
  ],

  /* ---- 8.3 Dressing the Track ---- */
  dressing: [
    {
      id: "ds-secret",
      type: "explain",
      body:
        "Time for the best secret in this course: genre lives " +
        "astonishingly shallow. The notes you wrote — your melody, your " +
        "chords, your bassline — aren't lo-fi notes or synthwave notes or " +
        "indie notes. They're just notes. Style is the dressing: the " +
        "tempo, the swing, the drum pattern, the voices everything is " +
        "played in. Change those and the same song moves to a different " +
        "world.",
    },
    {
      id: "ds-listen",
      type: "listen",
      prompt:
        "One tiny idea — home chord, then its sad twin — dressed two " +
        "ways. Note for note, the very same chords. Does it feel like the " +
        "same music twice?",
      options: [
        { id: "a", label: "A", phrase: DRESS_GOWN },
        { id: "b", label: "B", phrase: DRESS_DENIM },
      ],
      choices: [
        { id: "a", text: "A feels like mine" },
        { id: "b", text: "B feels like mine" },
        { id: "unsure", text: "same music, honestly" },
      ],
      responses: {
        a:
          "You reached for the slow gown — long floating strokes, " +
          "everything given room to bloom. Here's the trick, though: not " +
          "one pitch is different in B. It chops the same two chords into " +
          "quick, insistent stabs and suddenly the music is in a hurry. " +
          "Same harmony, different clothes — and the pull you just felt " +
          "is the beginning of your taste in style.",
        b:
          "The driving one — the same two chords as A, chopped into " +
          "quick stabs, and suddenly there's urgency where the other had " +
          "haze. Not one pitch changed. That's the whole lesson in eight " +
          "hits: rhythm and delivery are clothing, and the harmony " +
          "underneath doesn't care what it wears.",
        unsure:
          "That's an honest answer, and half right on purpose: the " +
          "pitches ARE identical, note for note — only the delivery " +
          "changes. Play them again asking a different question: which " +
          "room is each one happening in? A candlelit one and a moving " +
          "car, probably. That gap is what style is made of.",
      },
      note: "No right answer here at all — just a preview of the racks you're about to browse.",
    },
    {
      id: "ds-lenses",
      type: "explain",
      body:
        "So here are three worlds, ready to wear. Lo-fi: slow, swung, " +
        "dusty, the filter half-closed like a window shade. Synthwave: " +
        "four on the floor, neon-bright saws, night-drive momentum. " +
        "Indie: quick and clean, a live band in a bright room. Same " +
        "theory in every one of them — the doorbell still pulls, the " +
        "backbeat still claps.",
    },
    {
      id: "ds-audition",
      type: "explain",
      body:
        "The next moment hands you your own track — everything you've " +
        "built — and lets you hear it move into each world. The audition " +
        "IS the lesson: listen for what changes (the drums, the tempo, " +
        "the voices) and what survives untouched (every single note you " +
        "wrote).",
    },
    {
      id: "ds-keeper",
      type: "build",
      variant: "style",
      config: {
        lenses: ["lofi", "synthwave", "indie"],
        prompt:
          "Your track, three ways. Audition each one — it's the same " +
          "song every time, your song — then pick the world it lives in. " +
          "You can re-dress it in the Studio whenever the weather " +
          "changes.",
      },
      checks: [],
      keeperId: "trackStyle",
      keeperName: "your track's style",
    },
    {
      id: "ds-outro",
      type: "explain",
      body:
        "Your song has a wardrobe now — and you've heard with your own " +
        "ears that it stayed your song in every outfit. The theory was " +
        "never about a genre; it was about music. One door left. " +
        "Everything goes in its place.",
    },
  ],

  /* ---- 8.4 The Assembly ---- */
  assembly: [
    {
      id: "as-look-back",
      type: "explain",
      body:
        "Last door. Before anything else, listen to where you are: this " +
        "melody was once a three-note homecoming over a drone. Now it " +
        "rides chords you chose, a beat you built step by step, a " +
        "bassline that shakes hands with your kick. One thing remains — " +
        "deciding the order in which your song happens.",
      demo: {
        kind: "track",
        keeperId: "trackMelody",
        drone: true,
        label: "hear your melody, one more time",
      },
    },
    {
      id: "as-listen",
      type: "listen",
      prompt:
        "Two four-section songs built from the same materials. One stays " +
        "in the sunny room the whole time. One steps into the shadow for " +
        "its third stretch, then comes back. Which one makes coming home " +
        "feel like something?",
      options: [
        { id: "a", label: "A", phrase: ARR_ONE_WEATHER },
        { id: "b", label: "B", phrase: ARR_LEAVES_HOME },
      ],
      choices: [
        { id: "a", text: "A" },
        { id: "b", text: "B" },
        { id: "unsure", text: "neither returns for me" },
      ],
      responses: {
        a:
          "Interesting — A is the one that never leaves: four stretches " +
          "of the sunny room, and by the third, home has stopped feeling " +
          "like home because you never went anywhere. B leaves exactly " +
          "once — its third stretch steps into the shadow — and that " +
          "leaving is what makes the final return land. The oldest law " +
          "in this course, at its biggest scale.",
        b:
          "That's the shape — B spends its third stretch in the shadow, " +
          "so when the sunny room comes back you feel the door open. A " +
          "never leaves, and staying home the whole song makes home " +
          "invisible. You've known this since lesson one: the return is " +
          "only as good as the leaving. Now it's true at the scale of a " +
          "whole song.",
        unsure:
          "Then listen just for your own relief: somewhere in one of " +
          "them there's a moment where something comes BACK, and your " +
          "chest does a small unclench. The other can't give you that " +
          "moment, because it never went anywhere. The unclench is the " +
          "arrangement working.",
      },
      note: "Verse, chorus, bridge — every song form ever written is a variation on leave-and-return.",
    },
    {
      id: "as-map",
      type: "explain",
      body:
        "So the final craft is just this: put your sections in an order " +
        "that leaves and returns. You have three kinds of stretch to " +
        "place — A, the sunny half (your loop, your melody); B, the " +
        "shadow half (your minor loop, your B section); and quiet, where " +
        "the track drops to almost nothing so the next entrance lands. " +
        "Four slots. There are no wrong maps — but the ones with a " +
        "leaving tend to have the best homecomings.",
    },
    {
      id: "as-keeper",
      type: "build",
      variant: "arrange",
      config: {
        slots: 4,
        prompt:
          "The assembly. Tap each slot to choose its section — A, B, or " +
          "quiet — then play the whole shape in order. When it feels " +
          "like a song instead of a sequence, it's done. That feeling is " +
          "the finish line.",
      },
      checks: [{ id: "both-weathers", check: "arrangeHasContrast", args: [] }],
      keeperId: "arrangement",
      keeperName: "the arrangement",
    },
    {
      id: "as-full-circle",
      type: "echo",
      prompt:
        "One last echo, for old times' sake — these are the very first " +
        "calls you ever answered here, back when home was one note and " +
        "nothing else existed. The last one still stops short of home. " +
        "You know exactly what to do.",
      calls: [CALL_FALL_A, CALL_UNFINISHED],
      finisher: true,
    },
    {
      id: "as-outro",
      type: "explain",
      body:
        "That's the course. Head to the Studio and press “play the " +
        "whole song”: your sections, in your order, wearing your " +
        "style. Then listen once more, not as the person who made it but " +
        "as a stranger — and notice that nothing in it is a mystery. The " +
        "pulse, the backbeat, the roots under the chords, the doorbell " +
        "that pulls the loop around, the dusk in the B section, the " +
        "space in the quiet stretch — every layer of this song is " +
        "something you made and understand. Welcome home.",
    },
  ],
};

// The lessons that actually have playable moment data (the hub shows every
// lesson on the map; unauthored ones render as "coming soon"). Order matches
// the curriculum's linear unlock order.
export const AUTHORED_LESSON_IDS = [
  "home-note",
  "seven-neighbors",
  "leaving-returning",
  "steps-and-leaps",
  "colors-of-distance",
  "two-at-once",
  "contour",
  "say-it-again",
  "question-answer",
  "stacking",
  "seven-chords",
  "chord-jobs",
  "loops-journeys",
  "chord-tones",
  "targets-passing",
  "the-rewrite",
  "new-home",
  "minor-moves",
  "mood-switch",
  "the-grid",
  "drums",
  "bass",
  "roles-space",
  "builds-drops",
  "dressing",
  "assembly",
];
